import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/lib/constants';
import { PLAN_FACTS, formatLimit } from './plan-facts';

/**
 * Landing Page Content — Bilingual (EN / RO)
 *
 * 2026-07-08 REBUILD (generalist funnel — plan: .claude/plans/2026-07-08-landing-rebuild-plan.md,
 * copy: plan Addendum A, approved verbatim). All copy centralized; every user-facing string is
 * { en, ro }. Non-copy fields (href, id, icon, visual, name, time) remain plain strings.
 *
 * BINDING COPY RULES (drift-tested in __tests__/pricing-copy-sync.test.ts):
 * - NO em-dash (U+2014) in ANY string.
 * - NO "inclus în trial / included in trial" bot claims — the bot is a separate module.
 * - Every money/limit number derives from plans.ts (SUBSCRIPTION_PLANS, MODULE_CATALOG)
 *   or plan-facts.ts's formatVatLabel(priceIncludesVat) — never hand-typed against a
 *   stale memory.
 * - One canonical home per claim; VAT is a per-price derived fact at pricing.tiers[].
 *   vatLabel (2026-07-11 R3 — see the round-3 addendum below; the old global
 *   pricing.vatFootnote is deleted); CUI/Reg.Com. appear ONLY in footer.legalLine.
 * - No response-time promises, no invented metrics.
 *
 * TRUTHFULNESS NOTE: the trial runs 14 days on the plan chosen at signup (NOT "Pro for
 * everyone"); card required at registration, nothing charged until day 14; cancel anytime
 * self-serve from the account (no "2 clicks", no reminder-email promise).
 *
 * Keys marked "DEAD after 2026-07-08 rebuild" are kept ONLY because soft-unmounted or
 * not-yet-rewritten components still reference them (the tree must keep compiling while
 * lanes land). Delete each once its referencing component is rewritten or removed.
 *
 * 2026-07-10 LANE L0 (landing fleet rebuild — Judge 4 copy pack, .claude/handoffs/
 * landing-review-artifacts/judge-4-copy-pack.md): copy rewritten section-by-section per
 * the pack's SECTION-BY-SECTION TABLE. EXPAND-BEFORE-CONTRACT: every key still imported
 * by a not-yet-rewritten component (hero.rotator, hero.cta, pricing.quiz, modulesAI.*)
 * stays in place, marked "// DEAD-after-fleet-rebuild" — delete only once the owning
 * lane deletes/rewrites its component. New static hero copy lives at hero.outcome;
 * new hero vignette labels at hero.vignette; merged bot-module terms live at BOTH
 * botSection.moduleInfo (new, live home) and modulesAI.bot (old, DEAD, kept for
 * ModuleAISection.tsx) via the shared BOT_MODULE_* constants below — single source,
 * zero drift between the two homes.
 *
 * 2026-07-10 ROUND 2 (content barrier, .claude/handoffs/2026-07-10-landing-round2-
 * masterplan.md is the spec of record): runs ALONE before the round-2 lane fleet.
 * Alex's final H1 ships at hero.outcome.h1Line1/h1Line2 (new, two-line-capable keys);
 * hero.outcome.h1 stays populated (single-line merge of the two lines) ONLY because
 * HeroSection.tsx (not yet migrated by Lane 1) still reads it as one string — marked
 * "// DEAD-R2: remove after lane migration" below, same EXPAND-BEFORE-CONTRACT
 * discipline as the 2026-07-08 rebuild. e-Factura/invoicing dies as a topic
 * page-wide (Alex fork answer, masterplan §0) — every site in this file is swept;
 * component-level sites (TrustBlockSection.tsx's index-2 chip, IntegrationsDiagram.tsx,
 * compare pages, blog constants) are OUT of this file's scope and flagged for their
 * owning lane instead of edited here. Pricing gets a new anatomy (sharedIndex +
 * per-tier limitsPanel/planFeatures) sourced from constants/plan-facts.ts — a pure
 * adapter over SUBSCRIPTION_PLANS/MODULE_CATALOG (never hand-typed numbers).
 * pricing.thresholdLine is DELETED this same change (hard rider — the pack requires
 * pricing-copy-sync.test.ts to be re-targeted at the new limit-panel keys in the same
 * commit, never left orphaned); pricing.tiers[].priceRon is DELETED (RON estimates
 * retired). Both deletions are a KNOWN, NAMED, temporary break in PricingSection.tsx
 * (not this file's scope) that Lane L3 resolves when it rewrites that component's card
 * anatomy — called out explicitly in the masterplan, not an accident.
 *
 * file-size-ok: EXPAND-BEFORE-CONTRACT crossed the 1500-line arch-boundaries ceiling
 * this round (was 1453, pre-existing headroom under the old 1500 line). This is a
 * bilingual copy/constants file, not a component — growth is old-key retention (DEAD-
 * R2 markers) that consuming lanes delete as they migrate off each key; it shrinks
 * back down across the round-2 build, not a standing complexity problem to split.
 *
 * 2026-07-11 ROUND 3 (Wave 2 CONTENT-SURGEON pass, .claude/handoffs/2026-07-11-
 * landing-round3-masterplan.md is the spec of record; sequencing = D bot keys ->
 * E pricing/trust keys -> C social keys (no-op, kept local by the already-built
 * component) -> B featuresGrid/legacy deletions -> IntegrationsDiagram cluster
 * deletion): bot chapter script inverted to bot-first (botSection.chatDemo.
 * conversation), notification/price-surface/testSteps/illustrative* keys added,
 * configTogether rebuilt as a 4-step full-width process (bullets/freeLine
 * deleted); pricing rebuilt so Starter carries the COMPLETE feature inventory
 * (sharedIndex deleted, its rows folded in) while Pro carries two summary
 * groups, VAT becomes a per-tier derived vatLabel (vatFootnote deleted),
 * trustBlock.pillars[2] becomes the one bilingual (RO+EN) support statement
 * (trustBlock.supportLine deleted); new narrativeHandoffs top-level section
 * feeds Judge F's interleaved page-boundary bridges + the Pricing->Bot seam
 * divider (LandingPage.tsx's sole consumer); round-2 Product-OS duplicates
 * (featuresGrid, howItWorks, and their HowItWorksStep/FeatureBubble types) are
 * deleted now that FeatureGridSection.tsx is rebuilt against product-os-
 * content.ts instead — the zero-consumer IntegrationsDiagram{.tsx,.parts.tsx,
 * .mobile.tsx} cluster (their last reader) is deleted in the same pass, rg-
 * proven at every step. testCard.{title,reassurance} also deleted (round-2 test
 * card chrome the rebuilt SolutionSection.tsx Row 1 no longer renders).
 *
 * 2026-07-15 R5-P3 (Lane C0a content surgeon, r5-design-amendment.md — §6
 * "BOT — staged interactive flow" Decision 9 + §4 "GROWTH / LIGHT BAND"
 * [ADV-19]): `botSection.testSteps` was marked DEPRECATED-R5-P3 in place
 * (NOT deleted at build time — SolutionSection.tsx:285-286 was still a live
 * consumer) while `botSection.staged.{cta,line,noscript}` was ADDED for the
 * bot-chapter lane's 3-step grid-rows state machine that replaces it.
 * POST-REVIEW FIX ROUND: `testSteps` physically DELETED — a fresh grep
 * proved zero renderers (the bot-chapter lane's own rebuilt Row 1 no longer
 * reads it; only its own test's negative-assertion did, updated to a
 * hardcoded literal in the same commit) — completing the two-phase
 * EXPAND-BEFORE-CONTRACT this note started. `light.proof` (SECTION 9.5
 * below) gains `curveCaption` + `curveSrSummaryTemplate` for the real growth
 * curve; see that section's own R5-P3 amendment note for the closed-
 * manifest history.
 *
 * 2026-08-12 R8 (front-page restructure, spec of record: `.claude/handoffs/
 * 2026-08-12-r8-restructure-contract.md` - Albert's voice memo + Alex's three
 * confirmation rounds). COPY LANE ONLY, ADD-ONLY: six new top-level sections
 * (`differentiators`, `oneProcess`, `brainSystem`, `setup`, `whatsappModule`,
 * `statsBand`), one new hero sub-key (`hero.convergence`), six new
 * `narrativeHandoffs.bridges.*` seams, and an in-place rewrite of exactly three
 * existing leaves (`hero.outcome.h1Line1`/`h1Line2`/`sub`) to the new
 * positioning: CMG is not a place to STORE leads, it is the system that
 * CONTROLS the journey from lead to client to team activity, coordinated from
 * one place. NOTHING is deleted this pass. Keys whose only consumer dies in the
 * restructure (`pain`, `solution`, `features`, `socialProof`,
 * `integrationStrip`, `trustBlock`, and the five original bridges) are removed
 * by whichever lane removes their last renderer, in the same change, never
 * pre-emptively here. `trustBlock.pillars`/`.honestLine` were repointed at the
 * new module-level `TRUST_PILLARS`/`TRUST_HONEST_LINE` constants (byte-
 * identical values) so the R8 Setup carry-over is a single source instead of a
 * hand-kept mirror, the same discipline BOT_MODULE_TERMS already uses for its
 * two homes.
 */

// ─── Bot module terms — single canonical source (Judge 4 pack §"Bot module" row) ──
// Reused by botSection.moduleInfo (new, merged-into-bot-chapter home) AND modulesAI.bot
// (old key, DEAD-after-fleet-rebuild, kept only until ModuleAISection.tsx is deleted).
// Numbers derive from PLAN_FACTS.bot (plan-facts.ts -> MODULE_CATALOG.ai_lead_onboarding
// in plans.ts) — never hand-typed; drift-tested as a belt by pricing-copy-sync.test.ts.
// 2026-07-10 round 2 (copy ruling "Minor bot-block rulings"): reordered price-first
// (was trial-first) — the rows read as a commercial term sheet, not a countdown.
const BOT_MODULE_HEADLINE = {
  en: 'The WhatsApp bot is separate from the CRM.',
  ro: 'Botul WhatsApp este separat de CRM.',
};
const BOT_MODULE_TERMS = [
  {
    en: `€${PLAN_FACTS.bot.monthlyPrice} per month, ${PLAN_FACTS.bot.includedConversations} conversations included`,
    ro: `${PLAN_FACTS.bot.monthlyPrice} € pe lună, ${PLAN_FACTS.bot.includedConversations} conversații incluse`,
  },
  {
    en: `Then €${PLAN_FACTS.bot.overagePrice} for each extra ${PLAN_FACTS.bot.overageBlockSize} conversations`,
    ro: `Apoi ${PLAN_FACTS.bot.overagePrice} € la fiecare ${PLAN_FACTS.bot.overageBlockSize} de conversații suplimentare`,
  },
  {
    en: `${PLAN_FACTS.bot.trialDays}-day trial, once per company`,
    ro: `Trial ${PLAN_FACTS.bot.trialDays} zile, o singură dată per firmă`,
  },
];
const BOT_MODULE_CTA = {
  en: 'Create your account and activate the bot',
  ro: 'Creează contul și activează botul',
};

// ─── Trust facts — single canonical source (2026-08-12 R8 §S) ────────────────
// R8 merges TrustBlockSection's duties into the new Setup section ("You do not
// receive an empty CRM"): the three glance-level trust pillars + the public-
// prices honest line relocate into `setup.trust` below. Both homes read THESE
// constants, exactly like BOT_MODULE_TERMS serves two homes above, so the
// carry-over is a single source and not a hand-kept mirror that can drift while
// the old section is still mounted. Values are the approved R3 copy, verbatim
// (`pricing-copy-sync.test.ts` asserts the honest line and pillar 3's bilingual
// support statement against these exact strings). When the integration lane
// unmounts TrustBlockSection.tsx, `trustBlock` drops and `setup.trust` keeps
// serving the same constants: no copy re-approval needed at that step.
const TRUST_PILLARS = [
  {
    icon: 'Building2',
    title: { en: 'CMG Frameworks SRL', ro: 'CMG Frameworks SRL' },
    // Registration numbers stay OUT of the pillar: CUI/Reg.Com. appear exactly
    // ONCE on the page, in footer.legalLine.
    body: {
      en: 'CMG Workflow CRM is operated by CMG FRAMEWORKS SRL, a Romanian company (SRL) registered under Law no. 31/1990.',
      ro: 'CMG Workflow CRM este operat de CMG FRAMEWORKS SRL, societate românească (SRL) înregistrată conform Legii nr. 31/1990.',
    },
  },
  {
    icon: 'Shield',
    title: { en: 'Payments via Stripe', ro: 'Plăți prin Stripe' },
    body: {
      en: 'Card payments are processed securely through Stripe, an international payment processor.',
      ro: 'Plățile cu cardul sunt procesate securizat prin Stripe, procesator internațional de plăți.',
    },
  },
  {
    icon: 'Users',
    // 2026-07-11 R3 (r3-judge-E-verdict.md Decision 6/11): the page's ONE
    // support statement, bilingual (Romanian + English), never a tier feature
    // row.
    title: {
      en: 'Support in Romanian and English',
      ro: 'Suport în limba română și în limba engleză',
    },
    body: {
      en: 'A teammate helps you with setup, then a real person answers you in the language you choose.',
      ro: 'Un membru al echipei te ajută la configurare, iar apoi îți răspunde o persoană reală, în limba pe care o alegi.',
    },
  },
];
const TRUST_HONEST_LINE = {
  en: 'Public prices, always visible',
  ro: 'Prețuri publice mereu vizibile',
};

export const LANDING_CONTENT = {
  // ─── SECTION 1: NAVIGATION ────────────────────────────────────────────────
  nav: {
    logo: 'CMG Workflow CRM',
    // 2026-07-10 Judge 4 pack nav row: Produs / Prețuri / Bot / Întrebări (Partners
    // dropped from the top nav — it stays reachable from the footer's Partner
    // Program line). Anchors match the locked section-order decision (#pricing,
    // #bot, #faq).
    // 2026-08-12 R8 restructure: "Product" (#features) re-targeted to
    // #differentiators — FeatureGridSection (id="features") is DELETED this
    // round (the 7-step Product-OS stepper), and DifferentiatorsSection is
    // the first new content chapter after the hero, the nearest surviving
    // section for the same "what the product does" nav intent (integration
    // lane, `.claude/handoffs/2026-08-12-r8-restructure-contract.md` §4).
    links: [
      { label: { en: 'Product', ro: 'Produs' }, href: '#differentiators' },
      { label: { en: 'Pricing', ro: 'Prețuri' }, href: '#pricing' },
      { label: { en: 'Bot', ro: 'Bot' }, href: '#bot' },
      { label: { en: 'Questions', ro: 'Întrebări' }, href: '#faq' },
    ],
    // "Cont" nav label (Judge 4 pack) — LandingNavbar.tsx currently hardcodes its own
    // Sign-In string; this key is here for when that lane sources it from content.ts.
    account: { en: 'Account', ro: 'Cont' },
    cta: {
      // Compact CRM CTA (CTA dictionary) — destination-specific, not generic "Testează
      // gratuit" (pricing-copy-sync.test.ts asserts this). Shared by LandingNavbar.tsx
      // AND StickyMobileCTA.tsx (both read nav.cta.primary — updating here fixes both).
      primary: { en: 'Start CRM trial', ro: 'Începe trialul CRM' },
      // Not currently rendered (LandingNavbar has no secondary nav CTA slot) — updated
      // for when a bot-test nav CTA is wired in, per the CTA dictionary's "Bot section
      // link" entry.
      secondary: { en: 'Test the bot on WhatsApp', ro: 'Testează botul pe WhatsApp' },
    },
  },

  // ─── SECTION 2: HERO (2026-07-08 rebuild; outcome package 2026-07-10) ─────
  hero: {
    // ── Outcome package (2026-07-10, Judge 4 pack — locked decision 1) ──────────
    // Static SSR H1 + sub + dual CTAs + microcopy. This REPLACES the rotator/cta
    // pair below as the live hero copy; HeroRotator.tsx is deleted by Lane 1 (its
    // typewriter animation is banned by the motion spec). The "changeable text"
    // locked decision moves the source word into hero.vignette.sources instead,
    // synced with the vignette's card resets — never the H1 itself.
    outcome: {
      // NEW (2026-07-10 round 2, Alex's final H1 — polish-locked, masterplan §0):
      // two-line-capable keys for Lane 1's rewritten HeroSection.tsx. Each line is
      // one clause, period-split; the EN mirror is deliberately non-literal (by
      // design, not translation drift).
      //
      // 2026-08-12 R8 (restructure contract preamble + §H, Albert positioning
      // shift): the old pair ("You grow the business." / "The CRM keeps
      // everything on track.") sold a CRM that TIDIES UP behind you. Albert's
      // memo says the opposite: we are the system that CONTROLS the whole
      // journey from lead to client to team activity, coordinated from one
      // place. Rewritten in place (same two keys, same period-split clause
      // shape, still SSR-static for check-marketing-lcp-h1). EN is 7 words
      // total, the contract's ceiling; RO is 6 and stays indicative second
      // person, the native RO commercial register (a RO imperative headline
      // reads pushy, so the EN imperative is deliberately not mirrored
      // word-for-word).
      h1Line1: { en: 'Control the whole journey.', ro: 'Controlezi tot parcursul.' },
      h1Line2: {
        en: 'Lead, client, team.',
        ro: 'Lead, client, echipă.',
      },
      // 2026-07-10 round 2 (copy debate ruling, point 2 — email-extended anchor):
      // adds email as a fourth named source (transcript: "Gmail too narrow"); drops
      // the invoicing claim entirely (masterplan §0 e-Factura override).
      //
      // 2026-08-12 R8 (§H): the sub carries what the H1 compresses. The old
      // line enumerated INTAKE sources (Meta/TikTok/site/email) and stopped at
      // "one place"; those sources now render as the hero's own convergence
      // exhibit (`hero.convergence.sources` below), so keeping them here said
      // the same thing twice in one viewport. The sub instead states the three
      // facts the contract asks it to carry: one thread, one owner, full
      // history at every step, across leads, sales, clients and team work.
      sub: {
        en: 'Leads, sales, clients and team activity stay on one thread, with an owner and a full history at every step.',
        ro: 'Lead-urile, vânzările, clienții și activitatea echipei rămân pe același fir, cu responsabil și istoric la fiecare pas.',
      },
      // Dual CTA pair (locked decision 1/CTA dictionary) — CRM trial is the primary
      // conversion path, the WhatsApp bot test is the secondary, low-friction path.
      primaryCta: {
        label: { en: 'Start the 14-day CRM trial', ro: 'Începe trialul CRM de 14 zile' },
        // 2026-08-13: the full label wraps to 2 lines inside the 48px button at
        // 320 AND 375. This is the narrow-viewport variant, swapped by CSS only
        // (no JS, SSR-safe) below `min-[400px]`. It drops "CRM" — the hero H1
        // and sub directly above already say what the product is, so the button
        // only has to name the action and the offer. Precedent:
        // `light.demoForm.submitButtonShort` ("fits on one line at ~560px").
        // MEASURED, not modelled: this exact string renders `lines=1` in a
        // 280px button at true 320 RO (chrome emulate). A later pass replaced
        // it with a terser "Trial de 14 zile" on the strength of an estimated
        // 11px/char advance — that estimate was above the real one and the
        // trade cost a verb for no benefit, so this string is restored and the
        // measurement, not the model, is what the test now pins.
        labelShort: { en: 'Start the 14-day trial', ro: 'Începe trialul de 14 zile' },
        href: '/auth/register',
      },
      secondaryCta: {
        label: { en: 'Test the bot on WhatsApp', ro: 'Testează botul pe WhatsApp' },
        href: '#bot',
      },
      // Trial ribbon (2026-07-10 round 2, copy ruling point 3 — reassurance first):
      // zero-payment fact leads; the card-at-signup fact follows.
      microcopy: {
        en: 'You pay nothing during the trial. Your card is just saved at signup; the first charge comes after day 14.',
        ro: 'În trial nu plătești nimic. Cardul doar se salvează la înscriere, iar prima plată vine abia după ziua 14.',
      },
    },

    // ── Lead Journey vignette (2026-07-10, Judge 5 motion spec Rank 1) ──────────
    // Labels for the hero's animated product vignette (NEW LeadJourneyVignette.tsx,
    // Lane 1): one lead card moves NOU -> CONTACTAT -> OFERTĂ while the source pill
    // cycles through real, shipped lead sources. `card.id` is a plain display string
    // (locale-invariant, like the diagram's brand names) — not user-facing copy that
    // needs translation.
    vignette: {
      sources: [
        { en: 'Meta Ads', ro: 'Meta Ads' },
        { en: 'TikTok Ads', ro: 'TikTok Ads' },
        { en: 'Forms', ro: 'Formulare' },
        { en: 'Gmail', ro: 'Gmail' },
      ],
      newLeadBadge: { en: 'New lead', ro: 'Lead nou' },
      columns: {
        new: { en: 'NEW', ro: 'NOU' },
        contacted: { en: 'CONTACTED', ro: 'CONTACTAT' },
        offer: { en: 'OFFER', ro: 'OFERTĂ' },
      },
      card: {
        id: 'Lead #1042',
        business: { en: 'Cleaning', ro: 'Curățenie' },
        task: { en: 'Task: call', ro: 'Task: sună' },
        offer: { en: 'Offer', ro: 'Ofertă' },
      },
    },

    // ── Convergence exhibit (2026-08-12 R8 §H) ─────────────────────────────
    // Copy for the hero's RIGHT column: a light-paper hairline figure showing
    // seven fragmented lead sources collapsing through one amber CENTRALIZED
    // arrow into the product itself (Cosmos mark + CMG wordmark). This is the
    // page's opening argument for the R8 positioning: the sources are real
    // shipped ingest paths, the red chips are the real failure modes Albert
    // reports, and the terminus is CMG Workflow as the point of convergence.
    //
    // `id` is a plain, locale-invariant slot key (same convention as
    // `hero.vignette.card.id` and `integrationStrip.platforms[].name`) — the
    // rendering lane keys its icon map off it. `name` and `chip` ARE
    // user-facing, so both are bilingual leaves; RO chips are native failure
    // phrasing ("Pierdut în inbox"), never a word-for-word translation of the
    // EN. No metric, no time promise, no invented number anywhere in here.
    convergence: {
      kicker: { en: 'TODAY, SCATTERED', ro: 'ASTĂZI, ÎMPRĂȘTIAT' },
      fragmentedTitle: {
        en: 'Where your leads live now',
        ro: 'Unde stau lead-urile tale acum',
      },
      fragmentedTag: { en: 'Seven places', ro: 'Șapte locuri' },
      sources: [
        {
          id: 'meta',
          name: { en: 'Meta Ads form', ro: 'Formular Meta Ads' },
          chip: { en: 'Lost in inbox', ro: 'Pierdut în inbox' },
        },
        {
          id: 'tiktok',
          name: { en: 'TikTok instant forms', ro: 'Formulare instant TikTok' },
          chip: { en: 'Late follow-up', ro: 'Follow-up întârziat' },
        },
        {
          id: 'website',
          name: { en: 'Website inquiries', ro: 'Cereri de pe site' },
          chip: { en: 'No owner', ro: 'Fără responsabil' },
        },
        {
          id: 'email',
          name: { en: 'Personal email', ro: 'Email personal' },
          chip: { en: 'Unread thread', ro: 'Fir necitit' },
        },
        {
          id: 'whatsapp',
          name: { en: 'WhatsApp messages', ro: 'Mesaje WhatsApp' },
          chip: { en: 'Forwarded by hand', ro: 'Trimis mai departe manual' },
        },
        {
          id: 'spreadsheet',
          name: { en: 'Manual spreadsheets', ro: 'Tabele completate manual' },
          chip: { en: 'Outdated rows', ro: 'Rânduri depășite' },
        },
        {
          id: 'calendar',
          name: { en: 'Unsynced calendars', ro: 'Calendare nesincronizate' },
          chip: { en: 'Meeting not synced', ro: 'Întâlnire nesincronizată' },
        },
      ],
      arrowLabel: { en: 'CENTRALIZED', ro: 'CENTRALIZAT' },
      arrowSub: { en: 'Routed automatically', ro: 'Rutat automat' },
      // Product identity at the terminus. Both leaves carry the same brand
      // string on purpose (same precedent as `botSection.notification.appLabel`)
      // so every consumer can call `t()` uniformly instead of special-casing a
      // plain string in the middle of a translated composition.
      terminusName: { en: 'CMG Workflow', ro: 'CMG Workflow' },
      terminusSub: {
        en: 'Every lead lands here, routed and owned.',
        ro: 'Fiecare lead ajunge aici, rutat și cu responsabil.',
      },
    },
  },

  // ═══ R8 RESTRUCTURE (2026-08-12) ══════════════════════════════════════════
  // Spec of record: `.claude/handoffs/2026-08-12-r8-restructure-contract.md`
  // (Albert voice memo + 5 reference screenshots, 3 AskUserQuestion rounds with
  // Alex). The six keys below are the copy for the new section order:
  //   differentiators (§D) -> oneProcess (§P) -> brainSystem (§B) ->
  //   setup (§S) -> whatsappModule (§W) -> statsBand (§St)
  // Every one of them serves the same positioning: CMG is not a place to STORE
  // leads, it is the system that CONTROLS the journey from lead to client to
  // team activity, coordinated from one place.
  //
  // HONESTY LAW for this whole block (Constitution + R7 copy law, unchanged):
  // no invented CMG metric anywhere. The only numbers here live inside the §D2
  // attribution exhibit, and they are ILLUSTRATIVE SCENE CONTENT for a
  // fictional advertiser (labelled as such by `differentiators.d2.exhibit.tag`,
  // the same honesty marker `botSection.illustrativeCaption` carries) — never
  // presented as a CMG aggregate. Real platform numbers appear in exactly one
  // place on the page: `statsBand`, fed by the live SocialProofSnapshot, which
  // fails closed when the snapshot is null.
  //
  // RO is native marketing voice (Albert reads it), never a word-for-word
  // translation of the EN; EN is tight and claim-free. No em-dash anywhere
  // (pricing-copy-sync.test.ts sweeps every string in this file).

  // ─── R8 §D: DIFFERENTIATORS ───────────────────────────────────────────────
  // Two alternating copy/exhibit rows. `d1` is the routing argument (the CRM
  // adapts to your process), `d2` is the attribution argument (you know which
  // ad produced each client). Exhibit leaves describe a STATIC composed product
  // scene: `ifLabel`/`thenLabel` are the rule-configurator gutters, the
  // condition/action rows are what a real routing rule looks like in the app.
  differentiators: {
    eyebrow: { en: 'WHY IT IS DIFFERENT', ro: 'DE CE E DIFERIT' },
    h2: {
      en: 'Not just a place to store leads.',
      ro: 'Nu doar un loc unde ții lead-urile.',
    },
    sub: {
      en: 'Every lead enters the right process and stays connected to the marketing that generated it.',
      ro: 'Fiecare lead intră în procesul potrivit și rămâne legat de promovarea care l-a adus.',
    },
    d1: {
      kicker: { en: 'DIFFERENTIATOR 01', ro: 'DIFERENȚIATOR 01' },
      h3: {
        en: 'Every lead enters the right process.',
        ro: 'Fiecare lead intră în procesul potrivit.',
      },
      body: {
        en: 'Route by service, location, source, form answers, schedule, stage, team and responsible person. You do not adapt your business to the CRM. The CRM adapts to your process.',
        ro: 'Rutezi după serviciu, locație, sursă, răspunsurile din formular, program, etapă, echipă și responsabil. Nu îți adaptezi firma la CRM. CRM-ul se adaptează procesului tău.',
      },
      exhibit: {
        title: { en: 'Routing rule', ro: 'Regulă de rutare' },
        tag: { en: 'Illustrative scene', ro: 'Scenă ilustrativă' },
        ifLabel: { en: 'IF', ro: 'DACĂ' },
        conditions: [
          {
            label: { en: 'Service', ro: 'Serviciu' },
            value: { en: 'Office cleaning', ro: 'Curățenie birouri' },
          },
          {
            label: { en: 'Location', ro: 'Locație' },
            value: { en: 'Bucharest', ro: 'București' },
          },
          {
            label: { en: 'Budget', ro: 'Buget' },
            value: { en: 'Over 1,000 EUR', ro: 'Peste 1.000 EUR' },
          },
        ],
        thenLabel: { en: 'THEN', ro: 'ATUNCI' },
        actions: [
          { label: { en: 'Assign to the Sales team', ro: 'Atribuie echipei de vânzări' } },
          { label: { en: 'Owner: Ana Pop', ro: 'Responsabil: Ana Pop' } },
          { label: { en: 'Stage: Qualified', ro: 'Etapă: Calificat' } },
          { label: { en: 'Priority: High', ro: 'Prioritate: Ridicată' } },
        ],
      },
    },
    d2: {
      kicker: { en: 'DIFFERENTIATOR 02', ro: 'DIFERENȚIATOR 02' },
      h3: {
        en: 'Know which ad brought each client.',
        ro: 'Știi ce reclamă ți-a adus fiecare client.',
      },
      body: {
        en: 'Every lead keeps its source, campaign, ad and creative. The outcome stays connected to the promotion that produced it, so you can see what turns into clients and what only fills the list.',
        ro: 'Fiecare lead își păstrează sursa, campania, reclama și creativul. Rezultatul rămâne legat de promovarea care l-a adus, așa că vezi ce aduce clienți și ce doar umple lista.',
      },
      callout: {
        en: 'More leads do not automatically mean more sales.',
        ro: 'Mai multe lead-uri nu înseamnă automat mai multe vânzări.',
      },
      // ILLUSTRATIVE SCENE (see the honesty law at the top of this block): a
      // fictional advertiser's two campaigns, internally consistent on purpose
      // (420 leads x 3 EUR = 1,260 EUR per single client; 96 x 11 = 1,056 EUR
      // across 7 clients = 151 EUR each) so a reader who checks the arithmetic
      // finds it holds. These are NOT CMG numbers and never render without the
      // `tag` marker below.
      exhibit: {
        title: { en: 'Campaign attribution', ro: 'Atribuire pe campanii' },
        tag: { en: 'Illustrative scene', ro: 'Scenă ilustrativă' },
        campaignA: {
          name: { en: 'Campaign A', ro: 'Campania A' },
          tag: { en: 'Cheapest leads', ro: 'Cele mai ieftine lead-uri' },
          rows: [
            { label: { en: 'Leads', ro: 'Lead-uri' }, value: { en: '420', ro: '420' } },
            {
              label: { en: 'Cost per lead', ro: 'Cost pe lead' },
              value: { en: '3 EUR', ro: '3 EUR' },
            },
            { label: { en: 'Qualified', ro: 'Calificate' }, value: { en: '38', ro: '38' } },
            { label: { en: 'Clients', ro: 'Clienți' }, value: { en: '1', ro: '1' } },
            {
              label: { en: 'Cost per client', ro: 'Cost pe client' },
              value: { en: '1,260 EUR', ro: '1.260 EUR' },
            },
          ],
        },
        campaignB: {
          name: { en: 'Campaign B', ro: 'Campania B' },
          tag: { en: 'Highest ROI', ro: 'Cel mai bun ROI' },
          rows: [
            { label: { en: 'Leads', ro: 'Lead-uri' }, value: { en: '96', ro: '96' } },
            {
              label: { en: 'Cost per lead', ro: 'Cost pe lead' },
              value: { en: '11 EUR', ro: '11 EUR' },
            },
            { label: { en: 'Qualified', ro: 'Calificate' }, value: { en: '41', ro: '41' } },
            { label: { en: 'Clients', ro: 'Clienți' }, value: { en: '7', ro: '7' } },
            {
              label: { en: 'Cost per client', ro: 'Cost pe client' },
              value: { en: '151 EUR', ro: '151 EUR' },
            },
          ],
        },
        insight: {
          en: 'Campaign B pays more per lead and still brings seven times more clients.',
          ro: 'Campania B plătește mai mult pe lead și aduce de șapte ori mai mulți clienți.',
        },
      },
    },
  },

  // ─── R8 §P: ONE PROCESS ───────────────────────────────────────────────────
  // Albert's explicit instruction: NOT bubble/card blocks. Small elements
  // (mono numeral + title) with ONE short floating caption line under each,
  // joined by thin connectors. Captions stay one short line at 375px, so the
  // section reads as a quiet rail, not four cards.
  oneProcess: {
    eyebrow: { en: 'ONE PROCESS', ro: 'UN SINGUR PROCES' },
    h2: {
      en: 'One process, from the ad to the client.',
      ro: 'Un singur proces, de la reclamă la client.',
    },
    steps: [
      {
        num: '01',
        title: { en: 'The lead enters', ro: 'Lead-ul intră' },
        caption: {
          en: 'From Meta, TikTok, your website, email or WhatsApp.',
          ro: 'Din Meta, TikTok, site, email sau WhatsApp.',
        },
      },
      {
        num: '02',
        title: { en: 'The system routes it', ro: 'Sistemul îl rutează' },
        caption: {
          en: 'Your rules pick the team, the owner and the stage.',
          ro: 'Regulile tale aleg echipa, responsabilul și etapa.',
        },
      },
      {
        num: '03',
        title: { en: 'The team works it', ro: 'Echipa lucrează pe el' },
        caption: {
          en: 'Tasks, meetings and offers on the same thread.',
          ro: 'Taskuri, întâlniri și oferte, pe același fir.',
        },
      },
      {
        num: '04',
        title: { en: 'It becomes a client', ro: 'Devine client' },
        caption: {
          en: 'The whole history stays on the client account.',
          ro: 'Tot istoricul rămâne pe contul clientului.',
        },
      },
    ],
  },

  // ─── R8 §B: BRAIN SYSTEM ──────────────────────────────────────────────────
  // The feature recap, drawn as seven real modules converging on the Cosmos AI
  // core. `id` values are locale-invariant slot keys the diagram lane anchors
  // its geometry on (never rendered); `label` is the user-facing leaf. Every
  // node names a module that EXISTS in the product today, which is the whole
  // point of the section: it must read as product truth, not decoration.
  brainSystem: {
    eyebrow: { en: 'EVERYTHING IN ONE PLACE', ro: 'TOTUL ÎNTR-UN SINGUR LOC' },
    h2: {
      en: 'Everything your team does lives in one system.',
      ro: 'Tot ce face echipa ta stă într-un singur sistem.',
    },
    sub: {
      en: 'The modules write into the same data, and Cosmos AI reads all of it.',
      ro: 'Modulele scriu în aceleași date, iar Cosmos AI le citește pe toate.',
    },
    nodes: [
      { id: 'tasks', label: { en: 'Tasks and projects', ro: 'Taskuri și proiecte' } },
      { id: 'calendars', label: { en: 'Calendars and meetings', ro: 'Calendare și întâlniri' } },
      { id: 'offers', label: { en: 'Offers', ro: 'Oferte' } },
      { id: 'services', label: { en: 'Services', ro: 'Servicii' } },
      { id: 'teams', label: { en: 'Teams and ownership', ro: 'Echipe și responsabili' } },
      { id: 'communication', label: { en: 'Internal communication', ro: 'Comunicare internă' } },
      { id: 'attendance', label: { en: 'Time tracking and attendance', ro: 'Pontaj și prezență' } },
    ],
    core: {
      name: { en: 'Cosmos AI', ro: 'Cosmos AI' },
      line: {
        en: 'Ask it anything about your data. It also tells you what has been sitting still.',
        ro: 'Îl întrebi orice despre datele tale. Și îți spune ce a rămas pe loc.',
      },
    },
  },

  // ─── R8 §S: SETUP ─────────────────────────────────────────────────────────
  // "You do not receive an empty CRM." Same quiet numeral+caption register as
  // §P, one step per onboarding phase, then the amber primary CTA and the trust
  // tail. `trust` is the TrustBlock carry-over (see TRUST_PILLARS above): the
  // three glance-level facts and the public-prices line survive the merge here,
  // reading the same constants the old section reads, so nothing is silently
  // dropped when TrustBlockSection.tsx unmounts.
  setup: {
    eyebrow: { en: 'ASSISTED ONBOARDING', ro: 'CONFIGURARE ASISTATĂ' },
    h2: {
      en: 'You do not receive an empty CRM.',
      ro: 'Nu primești un CRM gol.',
    },
    sub: {
      en: 'We configure CMG Workflow around the process your company already has.',
      ro: 'Configurăm CMG Workflow pe procesul pe care firma ta îl are deja.',
    },
    steps: [
      {
        num: '01',
        title: { en: 'Tell us how your company works', ro: 'Ne spui cum lucrează firma ta' },
        caption: {
          en: 'Services, lead sources, responsible people and sales stages.',
          ro: 'Servicii, surse de lead-uri, responsabili și etape de vânzare.',
        },
      },
      {
        num: '02',
        title: { en: 'We configure the system', ro: 'Configurăm sistemul' },
        caption: {
          en: 'Pipelines, fields, services, team members, permissions and routing rules.',
          ro: 'Pipeline-uri, câmpuri, servicii, membri, permisiuni și reguli de rutare.',
        },
      },
      {
        num: '03',
        title: { en: 'We connect your sources', ro: 'Îți conectăm sursele' },
        caption: {
          en: 'Meta, TikTok, forms, your website and email.',
          ro: 'Meta, TikTok, formulare, site și email.',
        },
      },
      {
        num: '04',
        title: { en: 'Your team starts working', ro: 'Echipa începe să lucreze' },
        caption: {
          en: 'On your process, not on a blank account.',
          ro: 'Pe procesul vostru, nu pe un cont gol.',
        },
      },
    ],
    cta: { en: 'Start your setup', ro: 'Începe configurarea' },
    ctaHelper: {
      en: 'Create your account and a teammate helps you with the next steps.',
      ro: 'Îți creezi contul, iar un coleg te ajută cu pașii următori.',
    },
    trust: {
      pillars: TRUST_PILLARS,
      honestLine: TRUST_HONEST_LINE,
    },
  },

  // ─── R8 §W: WHATSAPP MODULE (section shell only) ──────────────────────────
  // Reframes the existing bot chapter as an optional module of the product, and
  // moves it BELOW Setup (Alex amendment to the contract). SHELL COPY ONLY: the
  // live WhatsAppDemoForm (`whatsappWedge.demoForm`) and the HeroChatReveal
  // transcript (`botSection.*`) keep their own keys untouched, because their
  // form logic, rate limits, staged reveal and success overlay are shipped,
  // tested behaviour that this restructure must not disturb.
  whatsappModule: {
    badge: { en: 'OPTIONAL MODULE', ro: 'MODUL OPȚIONAL' },
    // Dark exhibit frame header. Product name, same string in both locales
    // (same precedent as `botSection.notification.appLabel`) so the consumer
    // calls `t()` uniformly.
    frameTitle: { en: 'CMG WhatsApp Flow Builder', ro: 'CMG WhatsApp Flow Builder' },
    frameTag: { en: 'optional add-on module', ro: 'modul opțional' },
    h2: {
      en: 'Build the WhatsApp flow your process needs.',
      ro: 'Construiești fluxul WhatsApp de care are nevoie procesul tău.',
    },
    body: {
      en: 'The AI replies, collects the information you ask for, updates the CRM, follows up and transfers the conversation to the right person. The Flow Builder decides what happens, when it happens and where your team takes over. We configure it together with you.',
      ro: 'AI-ul răspunde, strânge informațiile pe care le ceri, actualizează CRM-ul, revine cu follow-up și transferă conversația persoanei potrivite. Flow Builder-ul stabilește ce se întâmplă, când se întâmplă și unde preia echipa ta. Îl configurăm împreună cu tine.',
    },
    // Reuses the CTA dictionary's bot-section wording verbatim (nav.cta.
    // secondary / hero.outcome.secondaryCta) — one destination, one phrasing.
    cta: { en: 'Test the bot on WhatsApp', ro: 'Testează botul pe WhatsApp' },
  },

  // ─── R8 §St: STATS BAND ───────────────────────────────────────────────────
  // Restyle of the light-band proof tiles. The VALUES stay the real
  // SocialProofSnapshot (leadsProcessed / clientsWon / conversionRate), which
  // fails closed to the em-dash unavailable state when the snapshot is null:
  // this key set only renames the surrounding chrome. `labels.rate` says
  // "lead-to-client rate" (contract §St) rather than the older generic
  // "conversion rate" at `light.proof.statLabels.conversion`, because the page
  // now spells out the lead-to-client journey everywhere above it.
  statsBand: {
    eyebrow: { en: 'PLATFORM ACTIVITY', ro: 'ACTIVITATE PE PLATFORMĂ' },
    h2: {
      en: 'What runs through CMG Workflow.',
      ro: 'Ce trece prin CMG Workflow.',
    },
    // R8 review FIX 6 (2026-08-12): the "Live" claim is DROPPED. The numbers
    // under this line come from `social-proof-snapshot.ts`, which serves a
    // Redis-cached aggregate with a 1h TTL — up to an hour stale, so "live" /
    // "live al platformei" was a claim the data cannot back (honest-copy law:
    // NO invented metrics, and no invented freshness either). The RO is
    // native, not a translation of the EN.
    sub: { en: 'Platform snapshot, real processed volume', ro: 'Cifre reale din platformă' },
    labels: {
      leads: { en: 'Leads processed', ro: 'Lead-uri procesate' },
      clients: { en: 'Clients won', ro: 'Clienți câștigați' },
      rate: { en: 'Lead-to-client rate', ro: 'Rată lead spre client' },
    },
  },

  // ─── SECTION 3 (NEW): INTEGRATION LOGO STRIP ──────────────────────────────
  // Borrowed-credibility band directly under the hero.
  // Logos: Meta · TikTok · WhatsApp · Gmail (platform names are plain strings — no {en,ro}).
  // Note: Google Forms excluded per romania-market knowledge (deprecated in product).
  integrationStrip: {
    heading: {
      en: 'Connects to the tools you already run leads on',
      ro: 'Se conectează direct la uneltele pe care deja rulezi campanii',
    },
    // Non-enumerated to avoid drift: the platforms[] array grows (6 entries now),
    // an enumerated "…WhatsApp and Gmail" line would under-count the icon strip
    // and violate the TRUTHFULNESS NOTE above.
    subheading: {
      en: 'All your lead sources, connected, not copy-paste.',
      ro: 'Toate sursele tale de lead-uri, conectate, nu copy-paste.',
    },
    // Platform names are brand-locked; alt text for each logo icon.
    // Outlook + Website added per Part B §3 / Part C §3 / Part E §E6 (owner decision 2026-06-17).
    platforms: [
      { name: 'Meta', alt: 'Meta Lead Ads' },
      { name: 'TikTok', alt: 'TikTok Lead Generation' },
      { name: 'WhatsApp', alt: 'WhatsApp Business' },
      { name: 'Gmail', alt: 'Gmail' },
      { name: 'Outlook', alt: 'Microsoft Outlook' },
      { name: 'Website', alt: 'Website forms' },
    ],
  },

  // ─── SECTION 4: PAIN (PAS REFRAME — SPEED-TO-LEAD LEAK STORY) ────────────
  pain: {
    // Section eyebrow
    eyebrow: {
      en: 'The real cost of slow follow-up',
      ro: 'Cât te costă, de fapt, follow-up-ul lent',
    },
    headline: {
      en: "You're paying for leads that go cold before anyone replies.",
      ro: 'Plătești pentru lead-uri care se răcesc înainte să apuce cineva să răspundă.',
    },
    subheadline: {
      en: 'Every minute between a click and a reply is money leaking out of your ad spend.',
      ro: 'Fiecare minut dintre click și primul răspuns înseamnă bani care ți se scurg din bugetul de ads.',
    },
    cards: [
      {
        icon: 'Moon',
        title: {
          en: 'Leads arrive at 2am. You reply at 10am.',
          ro: 'Lead-ul intră la 2 noaptea. Tu răspunzi la 10 dimineața.',
        },
        description: {
          en: 'By the time you open the inbox, the prospect already messaged three competitors and answered the one who replied first.',
          ro: 'Până deschizi inbox-ul, prospectul a scris deja la trei concurenți și a rămas cu cel care i-a răspuns primul.',
        },
      },
      {
        icon: 'Layers',
        title: {
          en: 'Five tabs, zero clarity.',
          ro: 'Cinci tab-uri, zero claritate.',
        },
        description: {
          en: 'Meta here, TikTok there, Gmail, WhatsApp, and no single place that tells you who is actually hot.',
          ro: 'Meta într-o parte, TikTok în alta, Gmail, WhatsApp, și niciun loc unde să vezi clar cine e cu adevărat interesat.',
        },
      },
      {
        icon: 'Clock',
        title: {
          en: 'You qualify by hand, lead by lead.',
          ro: 'Califici manual, lead cu lead.',
        },
        description: {
          en: 'The same four questions, typed out a hundred times a week. Your best hours go to copy-paste instead of closing.',
          ro: 'Aceleași patru întrebări, scrise de o sută de ori pe săptămână. Cele mai bune ore ale tale se duc pe copy-paste, nu pe vânzare.',
        },
      },
      {
        icon: 'AlertTriangle',
        title: {
          en: 'The hot ones get the same wait as the junk.',
          ro: 'Lead-urile bune stau la aceeași coadă cu cele slabe.',
        },
        description: {
          en: 'Without scoring, a serious buyer and a tire-kicker sit in the same line, and the buyer does not wait.',
          ro: 'Fără scoring, un client serios și un curios stau în aceeași listă, iar clientul serios nu așteaptă.',
        },
      },
    ],
    // 2026-07-10 round 2 (copy ruling §6c): dropped the unverified latency claim —
    // the pain vignettes above (2am/10am, "o sută de ori") stay, this transition line
    // was the one making an actual product-speed promise.
    transition: {
      en: "There's a way to answer every lead automatically, even while you sleep.",
      ro: 'Există o cale să răspunzi automat fiecărui lead, chiar și când dormi.',
    },
  },

  // ─── SECTION 5: WHATSAPP-AI WEDGE ─────────────────────────────────────────
  // Pre-rebuild copy (eyebrow/headline/body/bullets/doneWithYouNote/flowDiagram/
  // chatDemo) deleted 2026-07-08 cleanup pass: superseded by botSection +
  // configTogether, and their last referencing components (SolutionSection.tsx,
  // HeroChatReveal.tsx, LeadFlowDiagram.tsx) were rewritten or removed, so
  // nothing in src reads them anymore (grep-verified). EXCEPTION: demoForm.* is
  // LIVE — WhatsAppDemoForm.tsx keeps reading it (compat contract, do not move).
  whatsappWedge: {
    // ─── WHATSAPP DEMO FORM (LIVE — compat contract) ─────────────────────────
    // WhatsAppDemoForm.tsx reads every key below; the bot-section test card (Lane 3)
    // mounts that form inline. Shown when NEXT_PUBLIC_WHATSAPP_DEMO_ENABLED === 'true'.
    // EU/RO law: consent unchecked by default, identical disclosures across languages.
    // No response-time promises (2026-07-08 truth audit).
    demoForm: {
      heading: {
        en: 'Try the AI bot on your own number.',
        ro: 'Testează botul AI pe numărul tău.',
      },
      // 2026-07-10: trimmed the unverified "in a few seconds" speed claim (Judge 4
      // diction kill list) — keeps the instruction, drops the invented latency promise.
      subcopy: {
        en: 'Leave your name and phone. The bot writes to you on WhatsApp.',
        ro: 'Lasă-ți numele și telefonul. Botul îți scrie pe WhatsApp.',
      },
      // Compact pitch shown ABOVE the form fields (floating dock variant is unmounted
      // after the rebuild but the keys stay — DemoFormDock.tsx still compiles).
      floatingPitch: {
        en: 'See the bot live, free.',
        ro: 'Vezi botul live, gratuit.',
      },
      floatingPitchSub: {
        en: 'You get the exact message a lead gets, on WhatsApp. No call.',
        ro: 'Primești pe WhatsApp exact mesajul pe care îl primește un lead. Fără apel.',
      },
      // 2026-07-10 Judge 4 pack: short field labels ("Nume" / "Număr WhatsApp").
      nameLabel: {
        en: 'Name',
        ro: 'Nume',
      },
      namePlaceholder: {
        en: 'e.g. Ana Pop',
        ro: 'ex. Ana Pop',
      },
      phoneLabel: {
        en: 'WhatsApp number',
        ro: 'Număr WhatsApp',
      },
      phonePlaceholder: {
        en: '+40 7XX XXX XXX',
        ro: '+40 7XX XXX XXX',
      },
      // GDPR consent — one-time demo message; identical meaning across languages.
      // 2026-07-10 Judge 4 pack: shortened verbatim.
      consentLabel: {
        en: 'I agree to receive a demo message on WhatsApp from CMG Frameworks SRL. I can request data erasure.',
        ro: 'Accept să primesc un mesaj demo pe WhatsApp de la CMG Frameworks SRL. Pot cere ștergerea datelor.',
      },
      // 2026-07-10 Judge 4 pack CTA dictionary — "Bot form submit".
      submitButton: {
        en: 'Send me the demo message',
        ro: 'Trimite-mi mesajul demo',
      },
      // Short CTA for the compact floating bar — fits on one line at ~560px.
      submitButtonShort: {
        en: 'Send me the demo message',
        ro: 'Trimite-mi mesajul demo',
      },
      // 2026-07-10 round 2 (copy ruling §6c): dropped the unverified "in a few
      // seconds" latency claim. NOTE for the owning lane: this is one of three
      // near-duplicate reassurance lines around the same form (the other two are
      // botSection.testCard.reassurance and this same demoForm.subcopy above) — the
      // ruling wants only ONE surviving; content.ts can only retire the CLAIM in all
      // three, not the duplication itself (that needs a render-call deletion in
      // SolutionSection.tsx / WhatsAppDemoForm.tsx, out of this file's scope).
      reassurance: {
        en: 'The bot writes to you directly on WhatsApp, nothing to install.',
        ro: 'Botul îți scrie direct pe WhatsApp, fără nicio instalare.',
      },
      // Language picker aria-label
      langPickerLabel: {
        en: 'Language',
        ro: 'Limbă',
      },
      // Slot cue shown inside the reserved-height home slot (all breakpoints):
      // the single persistent form floats here as the user scrolls past.
      scrollCue: {
        en: 'Scroll down to try the demo',
        ro: 'Derulează pentru a testa demo-ul',
      },
      // aria-label for the floating (condensed) dock variant.
      floatingAriaLabel: {
        en: 'Try the AI bot demo form',
        ro: 'Formular demo bot AI',
      },
      // Hint shown in the reserved slot while the form is floating (isDocked=true).
      // Tells the visitor the form is following them so they don't lose it.
      floatingHint: {
        en: 'The form is following you below ↓',
        ro: 'Formularul te urmărește mai jos ↓',
      },
      // Success state — bot messages THEM (business-initiated); honest, no fake urgency.
      // Aligned with botSection.form.success (Addendum C §C3 state machine).
      successHeading: {
        en: 'Check WhatsApp',
        ro: 'Verifică WhatsApp-ul',
      },
      successCopy: {
        en: 'The bot is writing to you now. Open WhatsApp to see it.',
        ro: 'Botul îți scrie acum. Deschide WhatsApp ca să îl vezi.',
      },
      openWhatsAppButton: {
        en: 'Open WhatsApp',
        ro: 'Deschide WhatsApp',
      },
      // Error strings — friendly, not technical
      errorValidationName: {
        en: 'Please enter your name (at least 2 characters).',
        ro: 'Introdu te rog numele tău (cel puțin 2 caractere).',
      },
      errorValidationPhone: {
        en: 'Please enter a valid phone number (e.g. +40712345678).',
        ro: 'Introdu te rog un număr de telefon valid (ex. +40712345678).',
      },
      // 2026-07-10 Judge 4 pack: "conditional bot states" rewrite.
      errorConsentRequired: {
        en: 'Check the consent box to continue.',
        ro: 'Bifează acordul ca să continui.',
      },
      errorRateLimited: {
        en: 'Too many requests. Please wait a minute and try again.',
        ro: 'Prea multe cereri. Așteaptă un minut și încearcă din nou.',
      },
      errorNotConfigured: {
        en: 'The WhatsApp demo is not available right now. Please try again later.',
        ro: 'Demo-ul WhatsApp nu este disponibil momentan. Încearcă mai târziu.',
      },
      errorGeneric: {
        en: 'Something went wrong. Please try again.',
        ro: 'Ceva nu a mers. Încearcă din nou.',
      },
    },
  },

  // ─── BOT SECTION (2026-07-08 rebuild) ─────────────────────────────────────
  // Side-by-side pair (Addendum C §C3): chat demo (~60%) + live test card (~40%).
  // "Vezi detalii" anchors to #module-ai (ModuleAISection). The test card mounts
  // WhatsAppDemoForm inline (field labels/errors stay in whatsappWedge.demoForm).
  botSection: {
    // 2026-07-11 R3: NOW the live source (rulings Harm. d "optionalModule move")
    // — SolutionSection.tsx reads this key for its eyebrow pill directly. The
    // round-2 alternate home (featuresGrid.diagram.labels.optionalModule) is
    // deleted along with the rest of `featuresGrid` (see the SOLUTION-section
    // deletion note above); this was seeded with the identical "Modul opțional"
    // text specifically so the repoint needed zero content.ts edit.
    eyebrow: { en: 'Optional add-on', ro: 'Modul opțional' },
    // 2026-07-10 Judge 4 pack rewrite.
    headline: {
      en: "It replies on WhatsApp when your team can't.",
      ro: 'Răspunde pe WhatsApp când echipa nu poate.',
    },
    sub: {
      en: 'The bot asks the questions you choose and sends the conversation into the CRM.',
      ro: 'Botul pune întrebările alese de tine și trimite conversația în CRM.',
    },
    // DELETED 2026-07-10 round 2 (masterplan §1 "Bot block" row + copy ruling "Minor
    // bot-block rulings" — detailsLink button deleted, content.ts + SolutionSection.tsx
    // together): the terms are already visible in moduleInfo below, on the same page,
    // so the anchor-link button is redundant. KNOWN, NAMED, temporary break: this
    // deletion leaves SolutionSection.tsx:149 (`t(botSection.detailsLinkLabel)`)
    // referencing a key that no longer exists — Lane L4 (bot chapter owner) resolves
    // it by deleting that anchor-link block (SolutionSection.tsx:142-152), same as the
    // masterplan names both sites together.
    // testCard.{title,reassurance} DELETED 2026-07-11 R3 (Wave 2 content surgeon,
    // r3-judge-D-verdict.md file plan): the round-2 side-by-side test card this key
    // labeled no longer exists — SolutionSection.tsx's rebuilt Row 1 mounts the real
    // WhatsAppDemoForm directly, with its own testSteps copy above it (below). rg-
    // verified zero remaining consumers (only a historical comment in
    // WhatsAppDemoForm.tsx, not a render call) before deleting.
    // Test-form state machine (Addendum C §C3): idle → submitting → success (in-place
    // card, persists) | error (inline recoverable, values persist, no toast).
    // 2026-07-10 Judge 4 pack: "conditional bot states" rewrite.
    form: {
      submitting: { en: 'Sending the message…', ro: 'Trimit mesajul…' },
      // 2026-07-10 round 2 (copy ruling §6c "success state"): split across the two
      // rendered lines (WhatsAppDemoForm.tsx headline + body) so together they read
      // "Mesajul e pe drum. Deschide WhatsApp." verbatim.
      success: {
        headline: { en: 'Message is on its way', ro: 'Mesajul e pe drum' },
        body: {
          en: 'Open WhatsApp.',
          ro: 'Deschide WhatsApp.',
        },
      },
      error: {
        en: 'We could not send the message. Check the number and try again.',
        ro: 'Nu am putut trimite mesajul. Verifică numărul și încearcă din nou.',
      },
    },
    // ── Module terms, merged into the bot chapter (2026-07-10, locked decision 4) ──
    // Single canonical source: BOT_MODULE_HEADLINE/TERMS/CTA (module-level constants
    // above), also reused by the legacy modulesAI.bot key below so both homes stay
    // in sync with MODULE_CATALOG.ai_lead_onboarding — zero drift.
    moduleInfo: {
      headline: BOT_MODULE_HEADLINE,
      terms: BOT_MODULE_TERMS,
      cta: BOT_MODULE_CTA,
    },
    // ── Derived commercial price surface (2026-07-11 R3, r3-judge-D-verdict.md
    // §1.7.2-3) — rendered in SolutionSection.tsx's Row 1, beneath the phone.
    // Template expressions over PLAN_FACTS.bot, same discipline as
    // BOT_MODULE_TERMS above — never a hand-typed numeric literal.
    priceSurface: {
      // 2026-07-12 R3.5 (figure specs §3.3 "Price card — mass treatment"):
      // mono-label eyebrow atop the price card, the same MONO_LABEL recipe
      // used across the R3.5 filled-mock system.
      eyebrow: { en: 'WhatsApp module', ro: 'Modul WhatsApp' },
      amount: {
        en: `€${PLAN_FACTS.bot.monthlyPrice} / month`,
        ro: `${PLAN_FACTS.bot.monthlyPrice} € / lună`,
      },
      included: {
        en: `${PLAN_FACTS.bot.includedConversations} conversations included`,
        ro: `${PLAN_FACTS.bot.includedConversations} conversații incluse`,
      },
      overage: {
        en: `€${PLAN_FACTS.bot.overagePrice} / ${PLAN_FACTS.bot.overageBlockSize} extra`,
        ro: `${PLAN_FACTS.bot.overagePrice} € / ${PLAN_FACTS.bot.overageBlockSize} suplimentare`,
      },
      trial: {
        en: `${PLAN_FACTS.bot.trialDays}-day trial · once per company`,
        ro: `Trial ${PLAN_FACTS.bot.trialDays} zile · o singură dată per firmă`,
      },
    },
    // POST-REVIEW FIX ROUND (2026-07-15): the old 3 non-interactive
    // "test now" step cards (`testSteps`, 2026-07-11 R3) and their
    // `TEST_STEP_ICONS` (SolutionSection.tsx) are DELETED — a fresh grep
    // proved zero remaining renderers once the bot-chapter lane's rebuilt
    // Row 1 (the `staged` 3-step state machine below) replaced them; see
    // file header for the two-phase EXPAND-BEFORE-CONTRACT history.
    // Copy for the 3-step grid-rows state machine that REPLACED testSteps
    // (bot-chapter lane wires this in; content-surgeon only adds the keys
    // per the file-ownership split).
    // `cta`/`line` = Step 0's big CTA card copy; `noscript` = the no-JS
    // fallback line ([ADV-11]) shown beside the functional wa.me link.
    staged: {
      cta: { en: 'Try it yourself', ro: 'Testează-l și tu' },
      line: {
        en: 'The bot messages you first on WhatsApp.',
        ro: 'Botul îți scrie primul pe WhatsApp.',
      },
      noscript: {
        en: 'No JavaScript? Message us directly on WhatsApp',
        ro: 'Fără JavaScript? Scrie-ne direct pe WhatsApp',
      },
    },
    // iOS notification-banner app row (2026-07-11 R3, r3-judge-D-verdict.md
    // §1.1.1) — HeroChatReveal.tsx reads notification.appLabel/.now.
    notification: {
      appLabel: { en: 'WHATSAPP', ro: 'WHATSAPP' },
      now: { en: 'NOW', ro: 'ACUM' },
    },
    // Visible caption beneath the phone (§1.2.3) + the sr-only figcaption
    // summary for the aria-hidden notification/conversation subtree — HeroChatReveal.tsx
    // reads both.
    illustrativeCaption: { en: 'Illustrative conversation', ro: 'Conversație ilustrativă' },
    illustrativeSrSummary: {
      en: 'Illustrative WhatsApp conversation: the bot confirms the submitted form, asks a few qualifying questions, then hands the conversation off to a teammate.',
      ro: 'Conversație ilustrativă pe WhatsApp: botul confirmă formularul trimis, pune câteva întrebări de calificare, apoi predă conversația unui coleg.',
    },
    chatDemo: {
      botLabel: { en: 'CMG AI bot', ro: 'Botul AI CMG' },
      statusLabel: { en: 'online', ro: 'online' },
      // 2026-07-12 R3.5 (figure specs §4.4 "Add WhatsApp chrome"): the phone
      // shell was an mostly-empty shell (brief §5.1) — a top date/encryption
      // band + bottom composer bar are the two pieces of real WhatsApp chrome
      // a genuine screenshot always has. HeroChatReveal.tsx reads all three.
      dateLabel: { en: 'TODAY', ro: 'ASTĂZI' },
      encryptionNotice: {
        en: 'Messages are encrypted',
        ro: 'Mesajele sunt criptate',
      },
      composerPlaceholder: { en: 'Message', ro: 'Mesaj' },
      // 2026-07-10 LANE L3 (vanilla-motion rewrite): aria-label for the 44px replay
      // control exposed once the transcript finishes (Judge 5 motion spec §2: "vignettes
      // play once, retain their final state, and expose a 44px replay control"). Additive
      // only — no existing key changed (content.ts is Lane L0's file; this one key was
      // missing from the copy pack because the pack didn't anticipate an explicit replay
      // affordance, only Judge 2 finding 8's "provide a visible replay control").
      replayLabel: { en: 'Replay the conversation', ro: 'Redă din nou conversația' },
      // 2026-07-11 R3 (r3-judge-D-verdict.md §2.C): the bot speaks FIRST — turn 0
      // is carried by the notification banner (Decision 1.7 "no typing preamble")
      // and mirrors the real business-initiated public route
      // (src/app/api/marketing/whatsapp-demo/route.ts). Replaces the round-2
      // lead-first 8-turn script (`from: 'lead'` on turn 1), which contradicted
      // the real demo flow. Every WhatsApp timestamp displays 21:14 (D §2.C note:
      // the scene runs under a minute, so incrementing fake minute stamps would
      // be less authentic, not more). `time` is a display string.
      conversation: [
        {
          from: 'bot' as const,
          time: '21:14',
          text: {
            en: 'Hi! Thanks for filling in the form. I have a few questions so we can prepare your quote.',
            ro: 'Salut! Mulțumim că ai completat formularul. Am câteva întrebări ca să pregătim oferta.',
          },
        },
        {
          from: 'lead' as const,
          time: '21:14',
          text: {
            en: 'Sure.',
            ro: 'Sigur.',
          },
        },
        {
          from: 'bot' as const,
          time: '21:14',
          text: {
            en: 'What kind of space needs cleaning?',
            ro: 'Pentru ce tip de spațiu ai nevoie de curățenie?',
          },
        },
        {
          from: 'lead' as const,
          time: '21:14',
          text: {
            en: 'An office in Pipera.',
            ro: 'Un birou în Pipera.',
          },
        },
        {
          from: 'bot' as const,
          time: '21:14',
          text: {
            en: 'Roughly how many square metres is it?',
            ro: 'Aproximativ câți metri pătrați are?',
          },
        },
        {
          from: 'lead' as const,
          time: '21:14',
          text: {
            en: '300 sqm.',
            ro: '300 mp.',
          },
        },
        {
          from: 'bot' as const,
          time: '21:14',
          text: {
            en: 'When would you like to start?',
            ro: 'Când ai vrea să începi?',
          },
        },
        {
          from: 'lead' as const,
          time: '21:14',
          text: {
            en: 'Next month.',
            ro: 'Luna viitoare.',
          },
        },
        {
          from: 'bot' as const,
          time: '21:14',
          text: {
            en: 'Perfect, noted. A teammate will take over and follow up with your quote.',
            ro: 'Perfect, am notat. Un coleg preia conversația și revine cu oferta.',
          },
        },
      ],
    },
  },

  // ─── CONFIG-TOGETHER — DELETED 2026-08-12 (R8 review FIX 4) ───────────────
  // `configTogether` (headline / supportingLine / 4 steps) is GONE, together
  // with its sole consumer, SolutionSection.tsx's full-width closing chapter.
  // On the assembled R8 page it argued the same thing as §S Setup ("you do not
  // receive an empty account, we configure it around your process") and made a
  // THIRD numbered 4-step strip after OneProcess §P and Setup §S. The
  // bot-specific half of the claim survives verbatim in `whatsappModule.body`
  // ("We configure it together with you."). Key + consumer removed in ONE
  // change, so expand-before-contract does not apply.

  // `howItWorks` and `featuresGrid` (round-2 Product-OS duplicates) DELETED
  // 2026-07-11 R3 Wave-2 (r3-judge-B-verdict.md §2.8-2.9): FeatureGridSection.tsx
  // no longer imports LANDING_CONTENT at all (rebuilt against product-os-
  // content.ts's PRODUCT_OS_CONTENT instead) and the round-3 bot chapter reads
  // botSection.eyebrow, not featuresGrid.diagram.labels.optionalModule (D-lane's
  // optionalModule move, rulings Harm. d) — rg-verified zero remaining runtime
  // consumers of either key (only the IntegrationsDiagram cluster read
  // featuresGrid.diagram.labels.optionalModule, and that cluster was deleted in
  // this same Wave-2 pass as a zero-consumer orphan). The `HowItWorksStep` and
  // `FeatureBubble` types (below, in TYPE EXPORTS) are deleted with them.

  // ─── SECTION 7: SOLUTION (kept — 1B will merge/reorder) ──────────────────
  solution: {
    headline: {
      en: 'One CRM That Actually Gets Marketing Agencies',
      ro: 'Un CRM Care Înțelege Cu Adevărat Agențiile de Marketing',
    },
    subheadline: {
      en: 'Built by marketers who run ad campaigns daily. The most complete platform connecting lead acquisition, AI qualification, pipeline management, and client operations for agencies.',
      ro: 'Construit de marketeri care rulează campanii zilnic. Cea mai completă platformă care conectează achiziția de lead-uri, calificarea AI, managementul pipeline-ului și operațiunile cu clienții pentru agenții.',
    },
    promises: [
      {
        icon: 'Layers',
        title: { en: 'Every lead, one place', ro: 'Toate lead-urile, un singur loc' },
        description: {
          en: 'Meta, TikTok, Gmail, Forms: auto-import and unified inbox',
          ro: 'Meta, TikTok, Gmail, Formulare: import automat și inbox unificat',
        },
      },
      {
        icon: 'Bot',
        title: { en: 'AI that qualifies 24/7', ro: 'AI care califică 24/7' },
        description: {
          en: 'WhatsApp bot asks your questions, scores leads automatically',
          ro: 'Bot-ul WhatsApp pune întrebările tale, evaluează lead-urile automat',
        },
      },
      {
        icon: 'MessageSquare',
        title: { en: 'Ask anything, do anything', ro: 'Întreabă orice, fă orice' },
        description: {
          en: 'Cosmos AI runs your CRM with natural language commands',
          ro: 'Cosmos AI îți gestionează CRM-ul prin comenzi în limbaj natural',
        },
      },
    ],
    cta: { en: 'See How It Works', ro: 'Vezi Cum Funcționează' },
  },

  // ─── SECTION 8: FEATURES (kept — 1B merges/deletes individual items) ──────
  features: {
    id: 'features',
    items: [
      {
        badge: { en: 'INTEGRATIONS', ro: 'INTEGRĂRI' },
        title: { en: 'All Your Leads, One Inbox', ro: 'Toate Lead-urile Tale, Un Singur Inbox' },
        headline: {
          en: 'Stop juggling 5 tabs. Start closing deals.',
          ro: 'Nu mai jonglezi cu 5 tab-uri. Începe să închizi deal-uri.',
        },
        description: {
          en: 'Every lead from Meta Ads, TikTok, Gmail, and Google Forms flows into one unified dashboard, with built-in email import and native integrations.',
          ro: 'Fiecare lead din Meta Ads, TikTok, Gmail și Google Forms ajunge într-un dashboard unificat, cu import email integrat și integrări native.',
        },
        bullets: [
          {
            en: 'Real-time sync from all ad platforms',
            ro: 'Sincronizare în timp real din toate platformele de reclame',
          },
          {
            en: 'Auto-tagging by source and campaign',
            ro: 'Etichetare automată după sursă și campanie',
          },
          {
            en: 'Lead history across all touchpoints',
            ro: 'Istoricul lead-urilor din toate punctele de contact',
          },
        ],
        cta: { en: 'See integrations', ro: 'Vezi integrările' },
        visual: 'inbox',
      },
      {
        badge: { en: 'WHATSAPP AI', ro: 'WHATSAPP AI' },
        title: { en: 'WhatsApp AI That Never Sleeps', ro: 'WhatsApp AI Care Nu Doarme Niciodată' },
        headline: {
          en: 'Qualify leads at 3AM. Convert them at 9AM.',
          ro: 'Califică lead-uri la 3 dimineața. Convertește-le la 9.',
        },
        description: {
          en: "Our Gemini-powered bot qualifies leads 24/7 using your custom questions. It scores responses and hands off to your team when they're ready.",
          ro: 'Bot-ul nostru powered by Gemini califică lead-uri 24/7 cu întrebările tale personalizate. Scorează răspunsurile și predă echipei tale când sunt gata.',
        },
        bullets: [
          { en: 'Custom qualification questions', ro: 'Întrebări de calificare personalizate' },
          { en: 'Automatic lead scoring', ro: 'Scorare automată a lead-urilor' },
          {
            en: 'Smooth handoff to human when ready',
            ro: 'Transfer fluid către om când e momentul potrivit',
          },
        ],
        cta: { en: 'See AI in action', ro: 'Vezi AI-ul în acțiune' },
        visual: 'whatsapp',
      },
      {
        badge: { en: 'COSMOS AI', ro: 'COSMOS AI' },
        title: { en: 'Cosmos AI, Your CRM Copilot', ro: 'Cosmos AI, Copilotul Tău CRM' },
        headline: {
          en: 'Just ask. Cosmos handles the rest.',
          ro: 'Doar întreabă. Cosmos se ocupă de rest.',
        },
        description: {
          en: '60+ operations, zero learning curve. Search leads, create tasks, update records, run reports, all with natural language.',
          ro: '60+ operațiuni, curbă de învățare zero. Caută lead-uri, creează task-uri, actualizează înregistrări, generează rapoarte, totul prin limbaj natural.',
        },
        bullets: [
          {
            en: '"Show me all Meta leads this week"',
            ro: '"Arată-mi toate lead-urile din Meta săptămâna asta"',
          },
          {
            en: '"Create a follow-up task for John"',
            ro: '"Creează un task de follow-up pentru Andrei"',
          },
          { en: '"What deals closed last month?"', ro: '"Ce deal-uri s-au închis luna trecută?"' },
        ],
        cta: { en: 'Try Cosmos AI', ro: 'Încearcă Cosmos AI' },
        visual: 'cosmos',
      },
      {
        badge: { en: 'PIPELINE', ro: 'PIPELINE' },
        title: {
          en: 'Lead Pipeline That Actually Works',
          ro: 'Pipeline de Lead-uri Care Chiar Funcționează',
        },
        headline: {
          en: 'From first touch to closed deal. Visually.',
          ro: 'De la primul contact la deal-ul închis. Vizual.',
        },
        description: {
          en: 'Drag-and-drop Kanban board with custom stages, bulk actions, and team assignment. See your entire pipeline at a glance.',
          ro: 'Board Kanban drag-and-drop cu etape personalizate, acțiuni în masă și alocare în echipă. Vezi tot pipeline-ul dintr-o privire.',
        },
        bullets: [
          {
            en: 'Custom stages for your workflow',
            ro: 'Etape personalizate pentru fluxul tău de lucru',
          },
          { en: 'Bulk actions for efficiency', ro: 'Acțiuni în masă pentru eficiență maximă' },
          { en: 'Team assignment and handoffs', ro: 'Alocare în echipă și transferuri' },
        ],
        cta: { en: 'See the pipeline', ro: 'Vezi pipeline-ul' },
        visual: 'pipeline',
      },
      {
        badge: { en: 'CLIENTS', ro: 'CLIENȚI' },
        title: { en: 'Client Management After the Close', ro: 'Management Clienți După Închidere' },
        headline: {
          en: 'Convert leads to lifetime customers.',
          ro: 'Transformă lead-urile în clienți pe viață.',
        },
        description: {
          en: 'Track deal values, set billing reminders, and manage custom fields. Everything you need to nurture relationships post-sale.',
          ro: 'Urmărește valorile deal-urilor, setează remindere de facturare și gestionează câmpuri personalizate. Tot ce ai nevoie pentru a cultiva relații după vânzare.',
        },
        bullets: [
          {
            en: 'Deal tracking and value history',
            ro: 'Urmărirea deal-urilor și istoricul valorilor',
          },
          { en: 'Billing reminders and alerts', ro: 'Remindere de facturare și alerte' },
          { en: 'Custom fields for your business', ro: 'Câmpuri personalizate pentru afacerea ta' },
        ],
        cta: { en: 'See client management', ro: 'Vezi managementul clienților' },
        visual: 'clients',
      },
    ],
  },

  // ─── SECTION 9: SOCIAL PROOF ───────────────────────────────────────────────
  // DEAD after 2026-07-08 rebuild - superseded by `partners` below; kept only while
  // SocialProofSection.tsx still reads it (Lane 5 rewrites it badge-only, id="parteneri").
  socialProof: {
    headline: {
      en: 'Built with Clinica de Marketing',
      ro: 'Construit împreună cu Clinica de Marketing',
    },
    subline: {
      en: 'Tested every day by a real Romanian performance team, built for any business that lives on leads.',
      ro: 'Testat zi de zi de o agenție reală de performance, gândit pentru orice afacere care trăiește din lead-uri.',
    },

    foundingPartner: {
      name: 'Clinica de Marketing',
      logo: '/logos/clinica-de-marketing.png',
      location: 'Meta Ads & TikTok · România',
      // Unverified testimonial — never re-render without Albert's sign-off (partners.quote
      // is the new conditional home; it ships EMPTY).
      quote: {
        en: 'We used to put someone on chasing Meta leads all day. Now the bot qualifies them overnight, and the team only calls people who actually want to talk.',
        ro: 'Aveam un om care toată ziua doar suna lead-urile de pe Meta. Acum botul le califică peste noapte, iar echipa sună doar oameni care chiar vor să discute.',
      },
      author: {
        en: 'Founder, Clinica de Marketing',
        ro: 'Fondator, Clinica de Marketing',
      },
      // "~2 min" metric KILLED (2026-07-08 truth audit: unmeasured) — emptied so nothing
      // untrue can render while SocialProofSection.tsx awaits its Lane 5 rewrite.
      metric: {
        value: '',
        label: {
          en: '',
          ro: '',
        },
      },
      highlight: { en: 'Founding partner', ro: 'Partener fondator' },
    },
  },

  // ─── PARTNERS (2026-07-08 rebuild) ─────────────────────────────────────────
  // Badge-only spotlight card (locked decision 5). The quote block renders ONLY when
  // quote is non-empty; it ships EMPTY until Albert provides real words. Never invent.
  // 2026-07-10 Judge 4 pack "Partner" row rewrite.
  partners: {
    headline: { en: 'Used every day', ro: 'Folosit zi de zi' },
    sub: {
      en: 'Clinica de Marketing has used CMG Workflow since September 2025.',
      ro: 'Clinica de Marketing folosește CMG Workflow din septembrie 2025.',
    },
    badges: {
      founder: { en: 'Founding partner', ro: 'Partener fondator' },
      tenure: { en: 'User since September 2025', ro: 'Utilizator din septembrie 2025' },
    },
    quote: { en: '', ro: '' },
    // NEW (2026-07-10 round 2, masterplan §1/§5): trust line renders at the bottom of
    // this section (band #0B0C0E carries proof + trust line together per the design
    // ruling); not yet read by SocialProofSection.tsx — Lane L3 wires it in when it
    // rebuilds the 42/58 hybrid layout.
    trustLine: {
      en: 'You will not get an empty CRM: we set it up together with your team, and real people answer your support messages, in Romanian.',
      ro: 'Nu primești un CRM gol: îl configurăm împreună cu echipa ta, iar la suport îți răspund oameni, în română.',
    },
    // NEW (2026-07-10 round 2, masterplan §1/§5): muted footnote INSIDE this section
    // (not the footer's existing affiliateNote, which stays as-is) — links /affiliate,
    // no commission numbers. Not yet read by SocialProofSection.tsx — same Lane L3.
    incomeLine: {
      text: {
        en: 'Work with businesses that could use CMG? You can refer them through our partner program.',
        ro: 'Lucrezi cu firme care ar avea nevoie de CMG? Le poți recomanda prin programul de parteneriat.',
      },
      href: '/affiliate',
    },
  },

  // ─── SECTION 9.5 (NEW): LIGHT INTERLUDE — serif quote + big-stats proof ───
  // R4-P3 (.claude/handoffs/landing-review-artifacts/r4/r4-p3-masterplan.md,
  // [AMENDED per D1] "closed content manifest") — the ONLY content.ts keys the
  // two light-band sections (SerifQuoteSection + the light-recast
  // SocialProofSection stats, Lane P3-3) may read. CLOSED SET (as amended by
  // [CRITICAL-1 fix, r4-p3-review-plan.md]: `light.quote` DROPPED its
  // `attribution` key — see the FLAG below):
  // light.quote.{leadClause,restClause},
  // light.proof.{headlineLead,headlineRest,
  // statLabels.{leads,clients,conversion},unavailable}. No fourth-KPI key
  // ([AMENDED per A6] — the snapshot only ever produces 3 real numbers).
  // Chapter-local figure fixture labels live in product-os-content.ts (P3-2's
  // file, NOT this one) — D1 is explicit both ways: this lane must not touch
  // product-os-content.ts, and P3-2 must not touch this file.
  //
  // FLAG (content-surgeon discovery, off-plan #27 — surfaced per Constitution
  // "stop when evidence contradicts the plan", not silently resolved
  // originally, THEN RESOLVED as CRITICAL-1 in r4-p3-review-plan.md — see
  // "RESOLUTION" below):
  // masterplan Law 8 / §2.6 assert the serif quote is "the REAL Clinica
  // testimonial (already approved/anchored)", ready to trim at a clause
  // boundary. Disk evidence says the opposite. `partners.quote` immediately
  // above ships EMPTY with "it ships EMPTY until Albert provides real words.
  // Never invent" (line 955 of this file). `socialProof.foundingPartner.quote`
  // below carries its own "Unverified testimonial — never re-render without
  // Albert's sign-off" guard. And the actual ruling of record,
  // `.claude/handoffs/landing-review-artifacts/r3-judge-C-verdict.md:233`,
  // reads verbatim: "KILL: no approved factual content. Do not revive
  // `socialProof.quote`, `.author`, or `.metric`." There is NO approved
  // personal Clinica testimonial anywhere on disk today — the masterplan's
  // premise is stale, not a green light to revive killed copy.
  // FIRST RESOLUTION ATTEMPT (superseded): `light.quote.*` was built from
  // copy already approved/live elsewhere (`partners.trustLine`, split
  // verbatim) with an `attribution` key reusing `foundingPartner.name` +
  // `partners.badges.founder`'s text. On review (CRITICAL-1,
  // r4-p3-review-plan.md) this did NOT clear the KILL ruling — it worked
  // around it: `partners.trustLine` is CMG's own second-person promise
  // ("you"/"we"), grammatically impossible as something a customer said,
  // yet rendering it through `SerifQuote`'s literal quotation-mark +
  // `<figcaption>`-byline grammar presented it AS IF Clinica said it. That
  // is the exact fabrication Law 8 bans, laundered through a new key name.
  // FINAL RESOLUTION: `light.quote` carries NO `attribution` key.
  // `leadClause`/`restClause` render as an UNATTRIBUTED editorial statement
  // (`SerifQuoteSection.tsx` no longer passes `SerifQuote`'s
  // `attributionName` prop, which is now optional precisely for this case —
  // see `ui-light/SerifQuote.tsx`). Zero invented facts, zero revived killed
  // copy, zero false attribution — satisfies Law 6 (APPROVED COPY CARRIES)
  // and Law 8 (no fabricated testimonials) without the earlier workaround. A
  // REAL, freshly Albert-approved Clinica testimonial remains a valid future
  // upgrade (`SerifQuote.attributionName` still accepts one) but needs its
  // own follow-up content.ts amendment, never a render-time invention.
  //
  // 2026-07-15 R5-P3 AMENDMENT (Lane C0a content surgeon,
  // r5-design-amendment.md §4 "GROWTH / LIGHT BAND", [ADV-19]): the CLOSED
  // SET above is EXTENDED, not broken — two keys added to `light.proof` for
  // the real growth curve the growth-motion lane wires in this same
  // workflow: `curveCaption` (neutral, non-claim caption replacing the old
  // illustrative label per Decision 8 "make the data real, then drop the
  // label") and `curveSrSummaryTemplate` (the sr-only summary sentence for
  // the real curve; `{from}`/`{to}` are plain string tokens the consuming
  // component replaces at render time with the snapshot's first/last real
  // monthly values — this file stays pure data, no Intl/date logic at
  // module-eval time). Both are populated bilingual leaves like every other
  // key here, so light-content-manifest.test.ts's "every leaf is bilingual"
  // sweep already covers them; its exact-key-set assertions are updated in
  // the same commit (that test file is this lane's own, per file
  // ownership) — this is an in-lane, spec-directed expansion of the
  // manifest, not the "stray key added by a later lane" the suite guards
  // against.
  light: {
    quote: {
      // Verbatim split of the already-approved, already-live
      // `partners.trustLine` (above) at its one natural clause boundary (the
      // colon) — not a rewrite, not new copy. Rendered UNATTRIBUTED
      // (CRITICAL-1 fix) — no `attribution` key; see the FLAG above.
      leadClause: {
        en: 'You will not get an empty CRM:',
        ro: 'Nu primești un CRM gol:',
      },
      restClause: {
        en: 'we set it up together with your team, and real people answer your support messages, in Romanian.',
        ro: 'îl configurăm împreună cu echipa ta, iar la suport îți răspund oameni, în română.',
      },
    },
    proof: {
      // Reuse/trim of SocialProofSection.tsx's local `PANEL_HEADLINE`
      // ("What runs through CMG Workflow" / "Ce trece prin CMG Workflow"),
      // reshaped into the light band's two-tone lead+continuation split
      // (masterplan §2.3) — FLAG: reshaped into a two-clause split, no new
      // claim added.
      headlineLead: { en: 'What runs through', ro: 'Ce trece prin' },
      headlineRest: { en: 'CMG Workflow.', ro: 'CMG Workflow.' },
      // Verbatim reuse of SocialProofSection.tsx's local `KPI_LABELS` — the
      // exact three real snapshot fields ([AMENDED per A6]:
      // leadsProcessed/clientsWon/conversionRate only, no fourth illustrative
      // KPI folded into this grid).
      statLabels: {
        leads: { en: 'Leads processed', ro: 'Lead-uri procesate' },
        clients: { en: 'Clients won', ro: 'Clienți câștigați' },
        conversion: { en: 'Conversion rate', ro: 'Rată de conversie' },
      },
      // Verbatim reuse of SocialProofSection.tsx's local
      // `SNAPSHOT_UNAVAILABLE` — the exact fail-closed line for
      // `snapshot === null` ([AMENDED per A6]: em-dash values + this line,
      // never invented illustrative values).
      unavailable: {
        en: 'The real snapshot is temporarily unavailable.',
        ro: 'Snapshotul real nu este disponibil momentan.',
      },
      // NEW 2026-07-15 R5-P3 (see the R5-P3 AMENDMENT note above `light:`
      // for the closed-manifest history): neutral caption for the real
      // growth curve — replaces the illustrative label per Decision 8
      // ("make the data real, then drop the label"). Stays neutral even if
      // the truthful series declines (r5-design-amendment.md §4) — never a
      // growth claim the data must flatter.
      // R5-P4 fix [ADV-23]: `{count}` is a literal string token (same
      // convention as `{from}`/`{to}` below) — the trailing window is now
      // BOUNDED to the platform's actual history (6-12 months, never
      // blindly 12), so a hardcoded "12" would misstate the timeframe for
      // any younger platform; the consuming component substitutes the real
      // `monthlyLeads.length` at render time.
      curveCaption: {
        en: 'Platform activity, last {count} months',
        ro: 'Activitate pe platformă, ultimele {count} luni',
      },
      // NEW 2026-07-15 R5-P3: sr-only summary for the real curve ([ADV-19]
      // a11y — animated digits are aria-hidden, this is the one stable
      // sr-exposed value). `{from}`/`{to}`/`{count}` are literal string
      // tokens; the consuming component (BigStatsMotion/BigStatCurve,
      // growth-motion lane) replaces them with the snapshot's first/last
      // real monthly lead counts and the series' real length at render time
      // — never interpolated here (this file has no access to the runtime
      // snapshot).
      curveSrSummaryTemplate: {
        en: 'Last {count} months: from {from} to {to} leads per month.',
        ro: 'Ultimele {count} luni: de la {from} la {to} lead-uri pe lună.',
      },
    },
  },

  // ─── SECTION 10: PRICING (2026-07-11 R3 rebuild) ──────────────────────────
  // r3-judge-E-verdict.md: Starter now carries the COMPLETE feature inventory
  // (sharedIndex folded in, plus what was Pro-only before); Pro carries two
  // summary groups only. VAT is a per-price DERIVED fact (plans.ts
  // priceIncludesVat -> plan-facts.ts formatVatLabel -> tier.vatLabel), never a
  // hand-typed rate. Support is a page-level Trust pillar statement, never a
  // tier feature row (trustBlock.pillars[2] below). Trial/card terms and the
  // Pricing->Bot seam kicker moved OUT of this section entirely — they render
  // once in Judge F's interleaved DisclaimerDivider (narrativeHandoffs.priced,
  // near the end of this file) per r3-debate-rulings.md Harm. b.
  pricing: {
    id: 'pricing',

    headline: {
      en: 'Pricing that keeps pace with the size of your business',
      ro: 'Prețuri care țin pasul cu dimensiunea firmei tale',
    },
    sub: {
      en: 'Starter includes the core CRM features. Pro adds higher limits and time tracking.',
      ro: 'Starter include funcțiile CRM de bază. Pro adaugă limite mai mari și pontaj.',
    },
    // Header-to-cards bridge strip (Decision 5) — the canonical CRM_SETUP_PROMISE
    // reused verbatim by the Bot chapter's configTogether.headline/supportingLine
    // and by narrativeHandoffs.priced below; no fourth wording anywhere.
    setupPromise: {
      en: 'You do not get an empty CRM. We configure it with you around your company’s process.',
      ro: 'Nu primești un CRM gol. Îl configurăm împreună, pe procesul firmei tale.',
    },

    // sharedIndex DELETED (Decision 2): the standalone "included in both plans"
    // card is gone — its truthful rows moved INTO Starter's featureGroups below,
    // alongside what was Pro-only before (dashboard/KPIs, Cosmos Alerts,
    // connected organizations). rg-verified zero remaining consumers (only
    // historical comments) before deleting.
    // vatFootnote DELETED (Decision 7): VAT is now per-price derived (tier.vatLabel
    // below), never a global "applies only where required" footnote.

    tiers: [
      {
        name: 'Starter',
        tagline: {
          en: `For businesses handling up to ${PLAN_FACTS.starter.leads} leads per month.`,
          ro: `Pentru firme care gestionează până la ${PLAN_FACTS.starter.leads} de lead-uri pe lună.`,
        },
        // Derives from PLAN_FACTS (plan-facts.ts), never a hand-typed literal.
        price: PLAN_FACTS.starter.priceLabel,
        period: { en: '/mo', ro: '/lună' },
        cta: { en: 'Start the 14-day Starter trial', ro: 'Începe trialul Starter de 14 zile' },
        // Narrow-viewport variant (2026-08-13). The tier buttons sit in a card,
        // so at 320 they are ~230px wide rather than the hero's 280 — the
        // tightest of the four sites, which is why this variant is shorter
        // again than the hero's. It drops the tier name, which the card title
        // states directly above it, and keeps the 14-day offer, which nothing
        // else on the card says. The full label with the tier name stays the
        // button's accessible name via `aria-label`, so nothing is lost to a
        // screen reader at any width. MEASURED: `lines=1` in a 230px button at
        // true 320 RO.
        ctaShort: { en: 'Start 14-day trial', ro: 'Trial de 14 zile' },
        popular: false,
        // VAT — Decision 7 derivation contract: plans.ts priceIncludesVat ->
        // plan-facts.ts formatVatLabel() -> here. Never a hand-typed rate/string.
        vatLabel: PLAN_FACTS.starter.vatLabel,
        limitsPanel: {
          leads: {
            label: { en: 'Leads per month', ro: 'Lead-uri pe lună' },
            value: formatLimit(PLAN_FACTS.starter.leads),
          },
          users: {
            label: { en: 'Users', ro: 'Utilizatori' },
            value: formatLimit(PLAN_FACTS.starter.users),
          },
        },
        // REPLACES `planFeatures` (DELETED — the single differentiator-row shape
        // is gone). Starter now owns the COMPLETE truthful inventory in 3 grouped
        // columns (Decision 2/3) — never locked/greyed/crossed-out.
        featureGroups: [
          {
            heading: { en: 'CRM AND LEADS', ro: 'CRM ȘI LEAD-URI' },
            rows: [
              {
                en: 'Leads from Meta, TikTok, forms, your website and email',
                ro: 'Lead-uri din Meta, TikTok, formulare, site și email',
              },
              {
                en: 'Kanban pipeline with custom stages',
                ro: 'Pipeline Kanban cu etape personalizate',
              },
              {
                en: 'Contacts and clients, with full history',
                ro: 'Contacte și clienți, cu istoric complet',
              },
              { en: 'CSV import and export', ro: 'Import și export CSV' },
            ],
          },
          {
            heading: { en: 'TEAM WORK', ro: 'MUNCA ECHIPEI' },
            rows: [
              { en: 'Tasks, calendar and meetings', ro: 'Task-uri, calendar și întâlniri' },
              { en: 'Team and internal chat', ro: 'Echipă și chat intern' },
              { en: 'Offers and service catalog', ro: 'Oferte și catalog de servicii' },
              { en: 'Connected organizations', ro: 'Organizații conectate' },
            ],
          },
          {
            heading: { en: 'INSIGHTS AND AI', ro: 'ANALIZĂ ȘI AI' },
            rows: [
              { en: 'Dashboard with KPIs and reports', ro: 'Dashboard cu KPI-uri și rapoarte' },
              { en: 'Cosmos AI included', ro: 'Cosmos AI inclus' },
              { en: 'Cosmos AI alerts', ro: 'Alerte Cosmos AI' },
            ],
          },
        ],
      },
      {
        name: 'Pro',
        popular: true,
        badge: { en: 'Most popular', ro: 'Cel mai ales' },
        tagline: {
          en: 'For businesses with higher volume and more users.',
          ro: 'Pentru firme cu volum mai mare și mai mulți utilizatori.',
        },
        price: PLAN_FACTS.pro.priceLabel,
        period: { en: '/mo', ro: '/lună' },
        cta: { en: 'Start the 14-day Pro trial', ro: 'Începe trialul Pro de 14 zile' },
        // Narrow-viewport variant — see the Starter tier's `ctaShort` note.
        ctaShort: { en: 'Start 14-day trial', ro: 'Trial de 14 zile' },
        vatLabel: PLAN_FACTS.pro.vatLabel,
        limitsPanel: {
          leads: {
            label: { en: 'Leads per month', ro: 'Lead-uri pe lună' },
            value: formatLimit(PLAN_FACTS.pro.leads),
          },
          users: {
            label: { en: 'Users', ro: 'Utilizatori' },
            value: formatLimit(PLAN_FACTS.pro.users),
          },
        },
        // Pro carries a Starter-parity checklist (r3.5 §4 Lever A, lead-mandated
        // height-balance fix — r35-stepper-spec.md §4.2): Pro genuinely includes
        // everything Starter has, so mirror Starter's 3 category headings as a
        // truthful parity summary + keep the one real Pro-only extra. This
        // REPLACES the old 2-group "EVERYTHING IN STARTER + EXTRA IN PRO" shape
        // (which left Pro ~170px shorter than Starter, an inverted void) — never
        // re-collapse to 2 groups without re-measuring TierCard height parity.
        featureGroups: [
          {
            heading: { en: 'CRM AND LEADS', ro: 'CRM ȘI LEAD-URI' },
            rows: [
              {
                en: "Everything from Starter's sources, pipeline, contacts and import/export",
                ro: 'Tot din Starter: surse, pipeline, contacte și import/export',
              },
            ],
          },
          {
            heading: { en: 'TEAM WORK', ro: 'MUNCA ECHIPEI' },
            rows: [
              {
                en: 'Tasks, calendar, meetings, team chat, offers and connected organizations',
                ro: 'Task-uri, calendar, întâlniri, chat de echipă, oferte și organizații conectate',
              },
            ],
          },
          {
            heading: { en: 'INSIGHTS AND AI', ro: 'ANALIZĂ ȘI AI' },
            rows: [
              {
                en: 'Dashboard, Cosmos AI and Cosmos AI alerts',
                ro: 'Dashboard, Cosmos AI și alerte Cosmos AI',
              },
            ],
          },
          {
            heading: { en: 'EXTRA IN PRO', ro: 'ÎN PLUS ÎN PRO' },
            rows: [
              {
                en: 'Time tracking and attendance for your team',
                ro: 'Pontaj și prezență pentru echipă',
              },
            ],
          },
        ],
      },
    ],

    // Assurance row — Stripe mechanics only now (Decision 8/10): the trial/card
    // terms message moved OUT to narrativeHandoffs.priced (F-lane's interleaved
    // seam divider, below); this section keeps only the quiet payment-processor
    // line, never duplicated.
    assurance: {
      securePayment: { en: 'Secure payment through Stripe', ro: 'Plată securizată prin Stripe' },
    },
  },

  // ─── NARRATIVE HANDOFFS (2026-07-11 R3, r3-judge-F-verdict.md Decisions 2-4;
  // r3-debate-rulings.md Harm. b) — the five boundary micro-bridges plus the
  // priced Pricing->Bot seam divider that interleave the landing chapters.
  // LandingPage.tsx (Judge F's file) is the sole consumer, via
  // NarrativeHandoff/DisclaimerDivider (NarrativeHandoff.tsx). `sr` is the
  // sr-only funnel sentence carried by each bridge (aria-hidden visual, real
  // text alternative). `priced.kicker`/`trialLead`/`trialDetail` are the
  // Pricing->Bot seam's stage kicker + trial/card terms bubble copy (E-authored
  // per Harm. b — the bubble's ONLY home now; pricing.tiers above and
  // SolutionSection.tsx's bot terms never repeat it).
  narrativeHandoffs: {
    bridges: {
      // 2026-08-12 R8 integration: `leadCaptured` (Hero→Product) and
      // `organized` (Product→Proof) are DELETED — the R8 restructure
      // replaced both seams (FeatureGridSection, the section `leadCaptured`
      // led into, is deleted this round too) with `heroToDifferentiators`
      // below. `automated` (Bot→FAQ) is also DELETED — WhatsApp/Solution no
      // longer sits directly before FAQ in the new order (it moved to
      // directly-after-Setup), so that seam no longer exists. LandingPage.tsx
      // was each key's only real consumer (grep-verified) — this is the same
      // change that removed their last renderer, per this file's own R8
      // header note ("unused keys are deleted by whichever lane removes
      // their last renderer, in the same change").
      proven: {
        label: { en: 'Results proven', ro: 'Rezultate dovedite' },
        sr: {
          en: 'After the results, choose the right plan.',
          ro: 'După rezultate, alegi planul potrivit.',
        },
      },
      convinced: {
        label: { en: 'Everything is clear', ro: 'Totul e clar' },
        sr: {
          en: 'You have what you need; the decision follows.',
          ro: 'Ai informațiile necesare; urmează decizia.',
        },
      },

      // ── R8 seams (2026-08-12, restructure contract "New section order") ────
      // Six NEW bridges for the six NEW seams the restructure creates. Same
      // {label, sr} shape as the two survivors above, same consumption
      // contract (`label` -> NarrativeHandoff's visible chip, `sr` -> its
      // sr-only funnel sentence). Keys are named after the seam they span so
      // a section lane can reach for the right one without reading this
      // file. `proven` and `convinced` above are the only two of the five
      // original bridges that survive the new order (see the deletion note
      // above for the other three).
      // Each `sr` states the argument that just closed and the one that opens,
      // so a screen-reader user gets the page's spine, not decoration.
      heroToDifferentiators: {
        label: { en: 'Everything converges', ro: 'Totul converge' },
        sr: {
          en: 'The scattered sources arrive in one system, and the differences start there.',
          ro: 'Sursele împrăștiate ajung într-un singur sistem, iar de acolo încep diferențele.',
        },
      },
      differentiatorsToProcess: {
        label: { en: 'Rules in place', ro: 'Reguli stabilite' },
        sr: {
          en: 'With the rules set, every lead follows one process from the ad to the client.',
          ro: 'Cu regulile stabilite, fiecare lead urmează un singur proces, de la reclamă la client.',
        },
      },
      processToBrain: {
        label: { en: 'The team takes over', ro: 'Echipa preia' },
        sr: {
          en: 'That process runs on the modules the whole team works in every day.',
          ro: 'Procesul rulează pe modulele în care lucrează zilnic toată echipa.',
        },
      },
      brainToSetup: {
        label: { en: 'Configured for you', ro: 'Configurat pentru tine' },
        sr: {
          en: 'The system exists; next is how it gets set up around your company.',
          ro: 'Sistemul există; urmează cum îl configurăm pe firma ta.',
        },
      },
      setupToWhatsapp: {
        label: { en: 'Optional module', ro: 'Modul opțional' },
        sr: {
          en: 'After setup, WhatsApp automation is available as an optional module.',
          ro: 'După configurare, automatizarea pe WhatsApp este disponibilă ca modul opțional.',
        },
      },
      whatsappToInterlude: {
        label: { en: 'What it produces', ro: 'Ce produce' },
        sr: {
          en: 'This is what already runs through the platform.',
          ro: 'Asta trece deja prin platformă.',
        },
      },
    },
    priced: {
      kicker: { en: 'Plan chosen, no surprises', ro: 'Plan ales, fără surprize' },
      trialLead: { en: 'You pay nothing during the trial.', ro: 'În trial nu plătești nimic.' },
      trialDetail: {
        en: 'Your card is saved at signup, and the first payment is on day 14.',
        ro: 'Cardul se salvează la înscriere, iar prima plată este în ziua 14.',
      },
    },
  },

  // ─── SECTION 11: FAQ (reordered by RO buyer objections, card-timing FIRST) ─
  // 2026-07-10 Judge 4 pack FAQ reorder: Q1 trial, Q2 bot cost, Q3 CSV import, then
  // the remaining honest items trimmed to plain language (GDPR, Meta/TikTok sync,
  // cancel, Cosmos AI). The prior "bot speaks Romanian" item is REMOVED — its "se ia
  // după limba lead-ului" claim is unproven (judge-7 jargon census) and its bot-cost
  // content is now folded into Q2.
  faq: {
    id: 'faq',
    headline: {
      en: 'Frequently asked questions',
      ro: 'Întrebări frecvente',
    },
    items: [
      // Q1 — TRIAL TRUTH.
      {
        question: {
          en: 'How does the trial work?',
          ro: 'Cum funcționează trialul?',
        },
        answer: {
          en: 'You get 14 days on the plan you choose. A card is required at signup, but you are not charged until day 14. You cancel from your account.',
          ro: 'Ai 14 zile pe planul ales. Cardul se cere la înscriere, dar nu plătești până în ziua 14. Anulezi din cont.',
        },
        schemaEligible: true,
      },
      // Q2 — BOT COST. Question deliberately contains "WhatsApp AI bot" / "botul AI
      // WhatsApp" verbatim (order matters — pricing-copy-sync.test.ts locates this
      // item by that substring). Numbers derive from MODULE_CATALOG.ai_lead_onboarding.
      {
        question: {
          en: 'How much does the WhatsApp AI bot cost?',
          ro: 'Cât costă botul AI WhatsApp?',
        },
        answer: {
          en: "It's separate. It has a 7-day trial, costs €50 a month and includes 200 conversations. Then you pay €5 for every extra 50.",
          ro: 'Este separat. Are trial 7 zile, costă 50 € pe lună și include 200 conversații. Apoi plătești 5 € pentru fiecare 50 suplimentare.',
        },
        schemaEligible: true,
      },
      // Q3 — CSV IMPORT.
      {
        question: {
          en: 'Can I import my old leads?',
          ro: 'Pot importa lead-urile vechi?',
        },
        answer: {
          en: 'Yes, from a CSV file.',
          ro: 'Da, din CSV.',
        },
        schemaEligible: false,
      },
      // Q4 — GDPR / EU data residency (trimmed to plain language per judge-7 census).
      {
        question: {
          en: 'Where is my data stored, and is it GDPR-compliant?',
          ro: 'Unde sunt stocate datele mele și sunt conforme GDPR?',
        },
        answer: {
          en: 'Your data is hosted in the EU (Supabase, Frankfurt hosting) and never sold. You get a Data Processing Agreement (DPA) and can export or delete everything on request.',
          ro: 'Datele tale sunt găzduite în UE (Supabase, găzduire la Frankfurt) și nu sunt vândute niciodată. Ai un Acord de Prelucrare a Datelor (DPA) și poți exporta sau șterge tot, la cerere.',
        },
        schemaEligible: true,
      },
      // Q5 — Does it really sync Meta & TikTok? (trimmed)
      {
        question: {
          en: 'Does it actually pull leads from Meta and TikTok automatically?',
          ro: 'Chiar trage lead-urile de pe Meta și TikTok automat?',
        },
        // 2026-07-10 round 2 (copy ruling §6c): dropped the unverified latency claim.
        answer: {
          en: 'Yes. Connect your accounts once, and every lead lands in the CRM automatically, with its source.',
          ro: 'Da. Conectezi conturile o singură dată, iar fiecare lead ajunge automat în CRM, cu sursa lui.',
        },
        schemaEligible: true,
      },
      // Q6 — How do I cancel? (trimmed)
      {
        question: {
          en: 'How do I cancel?',
          ro: 'Cum anulez?',
        },
        answer: {
          en: 'Anytime, directly from your account, in billing settings. Cancel before day 14 and you are not charged at all.',
          ro: 'Oricând, direct din cont, din setările de facturare. Anulezi înainte de ziua 14 și nu ești taxat deloc.',
        },
        schemaEligible: true,
      },
      // Q7 — What is Cosmos AI? (trimmed; kept last — curiosity, not objection)
      {
        question: { en: "What's Cosmos AI?", ro: 'Ce este Cosmos AI?' },
        answer: {
          en: 'Cosmos AI searches and updates data in the CRM, in plain language.',
          ro: 'Cosmos AI caută și actualizează date în CRM, în limbaj obișnuit.',
        },
        schemaEligible: false,
      },
    ],
  },

  // ─── SECTION 12: FINAL CTA (2026-07-08 rebuild) ──────────────────────────
  // 2026-07-10 Judge 4 pack "Final CTA" row rewrite — dual-funnel CRM/bot close.
  finalCta: {
    // 2026-07-10 round 2 (register rule, masterplan §1): "lead-uri" page-wide; the
    // final H1 does not use "cerere" either, so the carve-out for it is moot.
    headline: {
      en: 'Never let another lead slip away.',
      ro: 'Nu mai lăsa niciun lead să se piardă.',
    },
    support: {
      en: 'Choose the CRM, the bot, or both.',
      ro: 'Alege CRM-ul, botul sau ambele.',
    },
    cta: {
      // Destination-specific — not generic "Testează gratuit" (pricing-copy-sync.test.ts).
      primary: { en: 'Start the 14-day CRM trial', ro: 'Începe trialul CRM de 14 zile' },
      // Narrow-viewport variant (2026-08-13) — see
      // `hero.outcome.primaryCta.labelShort` for the full rationale. Same
      // string as the hero's on purpose: it is the same offer and the same
      // destination, and two spellings of one CTA would be drift. Measured
      // `lines=1` at true 320 RO alongside the hero's.
      primaryShort: { en: 'Start the 14-day trial', ro: 'Începe trialul de 14 zile' },
      primaryMicro: {
        en: 'Separate trials.',
        ro: 'Trialuri separate.',
      },
      secondary: { en: 'Test the bot on WhatsApp', ro: 'Testează botul pe WhatsApp' },
    },
    // DEAD-after-fleet-rebuild (2026-07-10) — pack: "Remove the scheduler from this
    // dual-funnel block." Lane 5 removes the render call; the field stays populated
    // so FinalCTASection.tsx keeps compiling until that edit lands. NOTE for Lane 5:
    // the `secondary` CTA above now reads "Testează botul pe WhatsApp" but its <Link>
    // href is still hardcoded to "/demo" — needs to point at "#bot" once this section
    // is rewritten.
    tertiary: {
      en: 'Get your AI bot set up with you. Book 15 minutes.',
      ro: 'Configurăm botul AI împreună. Rezervă 15 minute.',
    },
  },

  // ─── SECTION 13 (NEW): TRUST BLOCK ────────────────────────────────────────
  // EU/GDPR + SRL entity + honest anti-dark-pattern statement.
  // CUI and Reg.com are placeholders — client must confirm real values before publish.
  // 2026-07-10 Judge 4 pack "Trust" row rewrite: 3 concrete pillars (company /
  // payments / invoicing) instead of company/GDPR/support — GDPR detail moves to the
  // FAQ (already covers it in depth); this block stays a glance-level proof point.
  //
  // 2026-07-10 ROUND 2 (masterplan §0 e-Factura override): pillar 3 is no longer
  // about invoicing — Alex's fork answer replaces it with a guided-setup + Romanian-
  // human-support pillar (fresh wording; deliberately NOT a verbatim repeat of the
  // proof-section trust line, which covers the same territory in prose).
  // RESOLVED (2026-07-11 R3 rebuild): TrustBlockSection.tsx has since been rebuilt —
  // there is no e-Factura badge or `index === 2` conditional anywhere in that
  // component, and 'Users' is already registered in its `iconMap` (no fallback
  // needed). Nothing outstanding for that lane.
  trustBlock: {
    headline: {
      en: "You know who you're working with.",
      ro: 'Știi cu cine lucrezi.',
    },
    // 2026-08-12 R8 §S: the pillar array and the honest line now read the
    // module-level TRUST_PILLARS / TRUST_HONEST_LINE constants (declared above
    // BOT_MODULE_*-style), because `setup.trust` carries the SAME three facts
    // into the new Setup section while this section is still mounted. Values are
    // byte-identical to the R3 copy this replaced (verbatim move into a shared
    // const, not a rewrite) — pricing-copy-sync.test.ts asserts both ends.
    pillars: TRUST_PILLARS,
    // 2026-07-11 R3 (r3-judge-E-verdict.md Decision 11): short exact public-prices
    // statement, drift-tested verbatim in pricing-copy-sync.test.ts.
    honestLine: TRUST_HONEST_LINE,
    // supportLine DELETED (Decision 6): the idea moved into pillar 3 above,
    // never duplicated on the page. rg/test-verified zero remaining consumers
    // (TrustBlockSection.tsx no longer renders it).
    // 2026-07-10: fixed the false bilingual-documents claim (hard constraint) — the
    // legal docs are Romanian-only today. Also doubles as the pack's "Close" line.
    legalLinks: {
      en: 'Terms, DPA and cookie policy, always visible.',
      ro: 'Termeni, DPA și politica de cookie-uri la vedere.',
    },
  },

  // ─── SECTION 14: FOOTER ───────────────────────────────────────────────────
  footer: {
    // Native footer key for the affiliate/partner note (Phase 1B).
    // socialProof.earlyAdopterCta removed Phase-1B-REMOVE — footer is now the sole owner.
    // 2026-07-10 Judge 4 pack "Footer" row — de-hyped affiliate line ("Recomanzi CMG?
    // Vezi programul de parteneriat" reads as one sentence with FooterSection.tsx's
    // hardcoded " · " separator between headline and cta).
    affiliateNote: {
      headline: {
        en: 'Refer CMG?',
        ro: 'Recomanzi CMG?',
      },
      description: {
        en: 'Join our Partner Program: refer businesses to CMG Workflow CRM and earn up to 40% recurring commission on every subscription. No CRM account needed. Just sign up, share your link, and start earning.',
        ro: 'Alătură-te Programului nostru de Parteneri: recomandă CMG Workflow CRM și câștigă până la 40% comision recurent din fiecare abonament. Nu ai nevoie de cont CRM. Înscrie-te, distribuie link-ul și începe să câștigi.',
      },
      cta: { en: 'See the partner program', ro: 'Vezi programul de parteneriat' },
    },
    tagline: {
      en: 'CRM for companies living on leads.',
      ro: 'CRM pentru firme care trăiesc din lead-uri.',
    },
    // Single legal line (2026-07-08 rebuild) — the ONLY place CUI / Reg. Com. appear on
    // the page. Language-invariant, rendered verbatim by FooterSection (Lane 5).
    legalLine: 'CMG Frameworks SRL · CUI: 52812612 · Nr. Reg. Com.: J2025083547000 · România, UE',
    // 2026-07-10: terser labels matching the Judge 4 pack's footer list, and nav-
    // consistent naming (Produs/Product, Întrebări/Questions). Partner Program +
    // Changelog stay (Partner Program's href is read by FooterSection's affiliate line).
    links: [
      { label: { en: 'Product', ro: 'Produs' }, href: '#features' },
      { label: { en: 'Pricing', ro: 'Prețuri' }, href: '#pricing' },
      // Partner Program link — affiliate CTA moves fully here in Phase 1B
      { label: { en: 'Partner Program', ro: 'Program Parteneri' }, href: '/affiliate' },
      { label: { en: 'Changelog', ro: 'Changelog' }, href: '/changelog' },
      { label: { en: 'Questions', ro: 'Întrebări' }, href: '#faq' },
      { label: { en: 'Contact', ro: 'Contact' }, href: SUPPORT_MAILTO },
    ],
    legal: [
      { label: { en: 'Privacy', ro: 'Confidențialitate' }, href: '/legal/privacy' },
      { label: { en: 'Terms', ro: 'Termeni' }, href: '/legal/terms' },
      { label: { en: 'Refunds', ro: 'Rambursări' }, href: '/legal/refund' },
      {
        label: { en: 'Acceptable Use', ro: 'Utilizare' },
        href: '/legal/acceptable-use',
      },
      {
        label: { en: 'Data', ro: 'Date' },
        href: '/legal/data-processing',
      },
      { label: { en: 'Cookies', ro: 'Cookie-uri' }, href: '/legal/cookies' },
      {
        label: { en: 'AI Literacy', ro: 'Alfabetizare AI' },
        href: '/legal/ai-literacy',
      },
    ],
    contact: SUPPORT_EMAIL,
    copyright: {
      en: `© ${new Date().getFullYear()} CMG Frameworks SRL. Toate drepturile rezervate.`,
      ro: `© ${new Date().getFullYear()} CMG Frameworks SRL. Toate drepturile rezervate.`,
    },
    business: {
      legalName: 'CMG Frameworks SRL',
      country: 'Romania, EU',
      email: SUPPORT_EMAIL,
      badges: [
        { en: 'GDPR Compliant', ro: 'Conform GDPR' },
        { en: 'EU Data Centers', ro: 'Centre de Date în UE' },
        { en: '256-bit Encryption', ro: 'Criptare 256-bit' },
      ],
    },
  },
};

// ─── TYPE EXPORTS ─────────────────────────────────────────────────────────────
// Existing exports preserved (components still read old key paths)
export type LandingContent = typeof LANDING_CONTENT;
export type PainCard = (typeof LANDING_CONTENT.pain.cards)[number];
export type Promise = (typeof LANDING_CONTENT.solution.promises)[number];
type Feature = (typeof LANDING_CONTENT.features.items)[number];
export type FoundingPartner = typeof LANDING_CONTENT.socialProof.foundingPartner;
export type PricingTier = (typeof LANDING_CONTENT.pricing.tiers)[number];
export type FAQItem = (typeof LANDING_CONTENT.faq.items)[number];

// New section types added in Phase 1A expand step
type IntegrationPlatform = (typeof LANDING_CONTENT.integrationStrip.platforms)[number];
// HowItWorksStep DELETED 2026-07-11 R3 Wave-2 with LANDING_CONTENT.howItWorks
// (see the deletion note above the SOLUTION section) — rg-verified zero
// remaining consumers.
export type TrustPillar = (typeof LANDING_CONTENT.trustBlock.pillars)[number];

// Phase 1B types
type FooterAffiliateNote = typeof LANDING_CONTENT.footer.affiliateNote;

// FeatureBubble DELETED 2026-07-11 R3 Wave-2 with LANDING_CONTENT.featuresGrid
// (see the deletion note above the SOLUTION section) — rg-verified zero
// remaining consumers.
type BotConversationTurn = (typeof LANDING_CONTENT.botSection.chatDemo.conversation)[number];

// 2026-07-10 round 2 pricing anatomy types — for Lane L3's card rewrite.
// PricingSharedIndexRow DELETED 2026-07-11 R3 (pricing.sharedIndex deleted,
// r3-judge-E-verdict.md Decision 2). PricingPlanFeatures DELETED 2026-07-11 R3
// (pricing.tiers[].planFeatures replaced by featureGroups, same decision) — rg-
// verified zero remaining consumers of either type.
export type PricingLimitsPanel = (typeof LANDING_CONTENT.pricing.tiers)[number]['limitsPanel'];
type PricingFeatureGroup = (typeof LANDING_CONTENT.pricing.tiers)[number]['featureGroups'][number];

// R4-P3 light-interlude types (D1 closed manifest) — for Lane P3-3's
// SerifQuoteSection / light-recast SocialProofSection consumption.
export type LightQuoteContent = typeof LANDING_CONTENT.light.quote;
export type LightProofContent = typeof LANDING_CONTENT.light.proof;
